import { useUserCountry } from "@src/packages/location/useUserCountry";
import React, {
  useState,
  useCallback,
  useMemo,
  useEffect,
  useRef,
} from "react";
import { isValidTime } from "@src/packages/utils/time";
import { useDateFormat } from "@src/packages/utils/useDateFormat";
import { useApiClient } from "@src/packages/api/louise-api";
import {
  Pressable,
  ScrollView,
  View,
  Text,
  Image,
  ActivityIndicator,
  useWindowDimensions,
  Platform,
} from "react-native";
import {
  X,
  Zap,
  ChevronDown,
  Clock,
  CalendarDays,
  Bell,
  Package,
  Route,
  NotebookText,
  Link2,
  type LucideIcon,
  Pill,
  Syringe,
  Droplets,
  Hand,
  Sun,
  Sunset,
  Moon,
  CloudMoon,
  Pencil,
  Plus,
  RotateCcw,
} from "lucide-react-native";
import * as ImagePicker from "expo-image-picker";
import {
  projectDocumentProcessingRunToPrefill,
  useDocumentSourceTransfer,
  type DocumentProcessingPrefillMedication,
  type DocumentSourceFile,
} from "@src/packages/documents/ingestion";
import { resolveUploadContentType } from "@src/packages/utils/fileUpload";
import {
  deserializeMedicationRoute,
  PATIENT_MEDICATION_ROUTES,
  type PatientMedicationRoute,
  serializeMedicationRoute,
  useCreateMedication,
  useUpdateMedication,
} from "@src/packages/care";
import { usePatientMedications } from "@src/packages/care/hooks/usePatientMedications";
import { isProviderOwnedMedication } from "@src/packages/care/medication-ownership";
import {
  extractMedicationShortName,
  isMedicationStopped,
  prependStoppedMedicationNote,
} from "@src/packages/care/medications";
import {
  AnchoredSelectMenu,
  CardSurface,
  MoonDetailRow,
  Row,
  CardSubtitleText,
  CardTitleText,
  CompactActionButton,
  compactSecondarySurfaceStyle,
  Dialog,
  DialogTitleText,
  DialogBodyText,
  DialogIcon,
  IconButton,
  InlineOptionPicker,
  PrimaryButton,
  MoonFilterChip,
  MoonToggle,
  MoonSearchInput,
  MoonFormField,
  DateField,
  MoonSegmentedControl,
  MoonUnitToggle,
  MoonValueInput,
  SecondaryButton,
  Tag,
  TextActionButton,
  type AnchoredSelectMenuItem,
  ActionBanner,
  radii,
  semanticColors,
  TextField,
  haptic,
  useToast,
  MoonSkeleton,
} from "@louise/moon-ui";
import {
  useMedicationProductVariations,
  useMedicationQuickAdd,
  findMedicationByName,
  resolveMedicationCatalogId,
} from "@src/packages/medications/catalog/useMedicationCatalog";
import {
  resolveTriggerShotPhase,
  buildTriggerShotPatch,
} from "@src/packages/care/trigger-shot-phase";
import {
  usePhases,
  useUpdatePhase,
} from "@src/packages/patients/hooks/usePhases";
import type { MedicationCatalogItem as DbMedication } from "@src/packages/medications/catalog/useMedicationCatalog";
import { addMedicationSchema, parseDoseInput } from "@src/packages/schemas";
import { getMedicationByBrandName } from "@src/packages/content";
import { getTodayString as getToday } from "@src/packages/utils/date";
import pillCapsuleArtwork from "../../../../assets/images/pill-capsule.png";
import injectionPenArtwork from "../../../../assets/images/injection-pen.png";
import vialSyringeArtwork from "../../../../assets/images/vial-syringe.png";
import { serializeMedicationForm } from "@src/packages/care/medication-form";
import scanPrescriptionCamera from "../../../../assets/images/scan-prescription-camera-v2.png";
import scanPackBarcode from "../../../../assets/images/scan-pack-barcode-v2.png";
import { MedFormIcon } from "../rows/MedRow";
import { MedicationPackScanPanel } from "./MedicationPackScanPanel";
import { MedicationIdentitySection } from "./MedicationIdentitySection";
import {
  buildMedicationIdentityInput,
  createMedicationIdentityDraft,
  inferPatientMedicationForm,
  withProductName,
  type MedicationIdentityDraft,
} from "@src/packages/medications";
import type { MedicationPackScanResult } from "@src/packages/medications/catalog/useMedicationPackScan";
import { useTimezone } from "@src/packages/preferences";
import type { Cycle } from "@src/packages/cycles";
import { EMBER } from "../../../../onboarding/theme";
import { useTranslations } from "@src/packages/i18n";

import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";

// ============================================================================
// Types
// ============================================================================

/** Shape accepted for editing an existing medication */
export type EditMedicationData = {
  id: string;
  name: string;
  dose?: number | null;
  doseUnit?: string | null;
  /** Persisted patient-medication administration route, not delivery form. */
  route?: string | null;
  /** API medication form value (e.g., "pre_filled_pen", "injection_solution") */
  form?: string | null;
  timeTaken?: string[];
  startDate?: string;
  endDate?: string | null;
  notes?: string | null;
};

/**
 * Seed the form for a brand-new medication (a "create", not an edit) so a
 * caller can launch it pre-filled — e.g. a medication extracted off a
 * prescription PDF. Ignored when `editMedication` is set.
 */
export type PrefillMedicationData = {
  name: string;
  dose?: number | null;
  doseUnit?: string | null;
  startDate?: string;
  notes?: string | null;
};

export type AddMedicationInventoryForm =
  "pen" | "vial" | "pill" | "suppository" | "supplies";

export type AddMedicationInventoryEntry = {
  name: string;
  dosage: string;
  doseAmount: number | null;
  doseUnit: string | null;
  route: string | null;
  routeLabel: string;
  deliveryMethod: string | null;
  deliveryMethodLabel: string | null;
  form: AddMedicationInventoryForm;
  quantity: number;
  unitLabel: string;
  medicationId: string | null;
};

type Props = {
  visible: boolean;
  onClose: () => void;
  cycleId?: string | null;
  /** Initial start date when the flow is launched from a selected calendar day. */
  initialStartDate?: string;
  /** The recorded first day of the period, distinct from a treatment cycle date. */
  menstruationStartDate?: Cycle["menstruationStartDate"];
  /** Restricts historical backfill to the selected cycle's date window. */
  cycleDateBounds?: {
    startDate: string;
    endDate?: string | null;
  };
  phaseId?: string | null;
  /** Called when trigger shot flow wants to open AddAppointmentModal pre-filled */
  onRequestRetrievalAppointment?: (prefill: {
    type: string;
    subType: string;
    suggestedDate: string;
    suggestedTime: string;
  }) => void;
  /** Explicit medication role override (from treatment hub) */
  medicationRole?: "standard" | "trigger_shot" | null;
  /** When provided, opens in edit mode at the Configure step with pre-filled data */
  editMedication?: EditMedicationData | null;
  /** When provided, seeds the form at the Configure step as a new medication. */
  prefillMedication?: PrefillMedicationData | null;
  /** Inventory mode keeps the standard search/configure flow but adds stock-on-hand fields. */
  inventoryMode?: boolean;
  onInventoryMedicationAdded?: (
    entry: AddMedicationInventoryEntry,
  ) => void | Promise<void>;
  /** Fires after a successful create — orchestrators (e.g., the protocol-
   * adjustment swap/add flow) use this to write an audit-trail note. */
  onMedicationCreated?: (info: {
    name: string;
    dose: number | null;
    doseUnit: string | null;
  }) => void;
  /** Fires after a successful edit-mode update with both the new and prior
   * dose values, so callers can record dose changes. */
  onMedicationUpdated?: (info: {
    id: string;
    name: string;
    dose: number | null;
    doseUnit: string | null;
    previous: { dose: number | null; doseUnit: string | null };
  }) => void;
  /** Small banner shown at the top of the Configure step — e.g. "Replacing
   * Gonal-F" when this modal is opened as the second step of a swap flow. */
  replacingMedicationName?: string;
};

type Step = 0 | 1 | 2 | 3;

type TimePreset = {
  labelKey: string;
  value: string;
  icon: typeof Sun;
};

const TIME_PRESETS: TimePreset[] = [
  { labelKey: "add_medication.time_preset.morning", value: "08:00", icon: Sun },
  {
    labelKey: "add_medication.time_preset.afternoon",
    value: "12:00",
    icon: Sunset,
  },
  {
    labelKey: "add_medication.time_preset.evening",
    value: "18:00",
    icon: Moon,
  },
  {
    labelKey: "add_medication.time_preset.night",
    value: "21:00",
    icon: CloudMoon,
  },
];

// The time of day option that opens the custom time field. Preset ids are
// clock times, so this cannot collide with one.
/** The artwork a pack gets, from the form its dose form and container make. */
function packFormFor(pack: { doseForm: string; container: string }) {
  const form = inferPatientMedicationForm(pack);
  if (form === "pre_filled_pen" || form === "pre_filled_syringe") return "pen";
  if (form === "injection_solution" || form === "lyophilized_powder") {
    return "vial";
  }
  if (form === "vaginal_suppository") return "suppository";
  return "pill";
}

const CUSTOM_TIME_OPTION = "custom";

const PRESCRIPTION_TIME_KEYS: Record<string, string> = {
  morning: "add_medication.prescription_morning",
  afternoon: "add_medication.prescription_afternoon",
  evening: "add_medication.prescription_evening",
  night: "add_medication.prescription_night",
};

const DOSE_UNITS = [
  "mg",
  "IU",
  "mcg",
  "ml",
  "g",
  "units",
  "pills",
  "tablets",
  "capsules",
  "drops",
  "patches",
];

const DOSE_UNIT_KEYS: Record<string, string> = {
  units: "add_medication.dose_unit.units",
  pills: "add_medication.dose_unit.pills",
  tablets: "add_medication.dose_unit.tablets",
  capsules: "add_medication.dose_unit.capsules",
  drops: "add_medication.dose_unit.drops",
  patches: "add_medication.dose_unit.patches",
};

type RouteOption = { labelKey: string; value: string; icon: typeof Pill };

const ROUTE_PRESENTATION: Record<
  PatientMedicationRoute,
  Omit<RouteOption, "value">
> = {
  oral: { labelKey: "add_medication.route_option.oral", icon: Pill },
  subcutaneous: {
    labelKey: "add_medication.route_option.subcutaneous",
    icon: Syringe,
  },
  intramuscular: {
    labelKey: "add_medication.route_option.intramuscular",
    icon: Syringe,
  },
  intravaginal: {
    labelKey: "add_medication.route_option.vaginal",
    icon: Droplets,
  },
  transdermal: {
    labelKey: "add_medication.route_option.patch",
    icon: Hand,
  },
  intranasal: {
    labelKey: "add_medication.route_option.nasal",
    icon: Droplets,
  },
  sublingual: {
    labelKey: "add_medication.route_option.sublingual",
    icon: Pill,
  },
  topical: {
    labelKey: "add_medication.route_option.topical",
    icon: Hand,
  },
  intravenous: {
    labelKey: "add_medication.route_option.intravenous",
    icon: Syringe,
  },
  other: { labelKey: "add_medication.route_option.other", icon: Hand },
};

const ROUTE_OPTIONS: RouteOption[] = PATIENT_MEDICATION_ROUTES.map((route) => ({
  ...ROUTE_PRESENTATION[route],
  value: deserializeMedicationRoute(route),
}));
const ROUTES = ROUTE_OPTIONS.map((r) => r.value);

const INJECTABLE_ROUTES = ["subcutaneous", "intramuscular"];

type DeliveryMethodOption = { labelKey: string; value: string };

const INJECTION_DELIVERY_METHODS: DeliveryMethodOption[] = [
  {
    labelKey: "add_medication.delivery_method_option.pre_filled_syringe",
    value: "pre_filled_syringe",
  },
  {
    labelKey: "add_medication.delivery_method_option.pre_filled_pen",
    value: "pre_filled_pen",
  },
  {
    labelKey: "add_medication.delivery_method_option.injection_solution",
    value: "injection_solution",
  },
  {
    labelKey: "add_medication.delivery_method_option.lyophilized_powder",
    value: "lyophilized_powder",
  },
  {
    labelKey: "add_medication.delivery_method_option.multi_vial",
    value: "multi_vial",
  },
];

/** Maps content MedicationForm values to API form values */

/** Maps API form value to the value we send (multi_vial → other) */
function mapDeliveryMethodToApiForm(value: string) {
  return serializeMedicationForm(value === "multi_vial" ? "other" : value);
}

/** Below this, typing is not yet a search and the box is not drawn at all. */
const SEARCH_MIN_LENGTH = 2;
/** The one height the results box holds, in every one of its four states. */
const SEARCH_RESULTS_MAX_HEIGHT = 268;

type DosagePreset = { doses: number[]; unit: string; route: string };

/** Quick-select dosage presets + known route by medication name (case-insensitive) */
const DOSAGE_PRESETS: Record<string, DosagePreset> = {
  "gonal-f": {
    doses: [75, 150, 225, 300, 450, 900],
    unit: "IU",
    route: "subcutaneous",
  },
  follistim: {
    doses: [75, 150, 225, 300, 450, 900],
    unit: "IU",
    route: "subcutaneous",
  },
  menopur: { doses: [75, 150, 225], unit: "IU", route: "subcutaneous" },
  cetrotide: { doses: [0.25, 3], unit: "mg", route: "subcutaneous" },
  ganirelix: { doses: [0.25], unit: "mg", route: "subcutaneous" },
  letrozole: { doses: [2.5, 5, 7.5], unit: "mg", route: "oral" },
  femara: { doses: [2.5, 5, 7.5], unit: "mg", route: "oral" },
  clomid: { doses: [50, 100, 150], unit: "mg", route: "oral" },
  clomiphene: { doses: [50, 100, 150], unit: "mg", route: "oral" },
  progesterone: { doses: [50, 100, 200], unit: "mg", route: "intramuscular" },
  endometrin: { doses: [100, 200], unit: "mg", route: "vaginal" },
  prometrium: { doses: [100, 200, 400], unit: "mg", route: "oral" },
  crinone: { doses: [90], unit: "mg", route: "vaginal" },
  ovidrel: { doses: [250], unit: "mcg", route: "subcutaneous" },
  "choriogonadotropin alfa": {
    doses: [250],
    unit: "mcg",
    route: "subcutaneous",
  },
  pregnyl: { doses: [5000, 10000], unit: "IU", route: "intramuscular" },
  novarel: { doses: [5000, 10000], unit: "IU", route: "intramuscular" },
  hcg: { doses: [5000, 10000], unit: "IU", route: "intramuscular" },
  "chorionic gonadotropin": {
    doses: [5000, 10000],
    unit: "IU",
    route: "intramuscular",
  },
  lupron: { doses: [0.25, 0.5, 1], unit: "mg", route: "subcutaneous" },
  leuprolide: { doses: [0.25, 0.5, 1], unit: "mg", route: "subcutaneous" },
  estrace: { doses: [1, 2, 4], unit: "mg", route: "oral" },
  estradiol: { doses: [1, 2, 4], unit: "mg", route: "oral" },
  dexamethasone: { doses: [0.5, 0.75, 1], unit: "mg", route: "oral" },
  prednisone: { doses: [5, 10, 20], unit: "mg", route: "oral" },
  metformin: { doses: [500, 850, 1000], unit: "mg", route: "oral" },

  // ─── French brand names (presets mirror French AMP practice) ───────
  décapeptyl: { doses: [0.1, 3, 11.25], unit: "mg", route: "subcutaneous" },
  decapeptyl: { doses: [0.1, 3, 11.25], unit: "mg", route: "subcutaneous" },
  ovitrelle: { doses: [250], unit: "mcg", route: "subcutaneous" },
  utrogestan: { doses: [100, 200], unit: "mg", route: "vaginal" },
  estima: { doses: [100, 200], unit: "mg", route: "vaginal" },
  progestan: { doses: [100, 200], unit: "mg", route: "vaginal" },
  provames: { doses: [1, 2], unit: "mg", route: "oral" },
  oromone: { doses: [1, 2], unit: "mg", route: "oral" },
  oestrofem: { doses: [1, 2], unit: "mg", route: "oral" },
  vivelledot: { doses: [25, 37.5, 50, 75, 100], unit: "mcg", route: "patch" },
  estrapatch: { doses: [40, 60, 80], unit: "mcg", route: "patch" },
  femsept: { doses: [50, 75, 100], unit: "mcg", route: "patch" },
  oesclim: { doses: [25, 50], unit: "mcg", route: "patch" },
  fostimon: { doses: [75, 150, 225], unit: "IU", route: "subcutaneous" },
  // Walked by the injection coach; the unit here is the label's, so the
  // record is born in the unit the dose guard will accept.
  luveris: { doses: [75, 150, 225], unit: "IU", route: "subcutaneous" },
  lubion: { doses: [25], unit: "mg", route: "subcutaneous" },
  prolutex: { doses: [25], unit: "mg", route: "subcutaneous" },
  elonva: { doses: [100, 150], unit: "mcg", route: "subcutaneous" },
  rekovelle: { doses: [6, 8, 10, 12], unit: "mcg", route: "subcutaneous" },
  pergoveris: { doses: [150, 300, 450], unit: "IU", route: "subcutaneous" },
  meriofert: { doses: [75, 150], unit: "IU", route: "subcutaneous" },
  ovaleap: {
    doses: [75, 150, 225, 300, 450, 900],
    unit: "IU",
    route: "subcutaneous",
  },
  bemfola: {
    doses: [75, 150, 225, 300, 450],
    unit: "IU",
    route: "subcutaneous",
  },
  pergotime: { doses: [50, 100, 150], unit: "mg", route: "oral" },
  enantone: { doses: [3.75, 11.25], unit: "mg", route: "intramuscular" },
  lovenox: { doses: [20, 40, 60, 80, 100], unit: "mg", route: "subcutaneous" },
  fragmin: {
    doses: [2500, 5000, 7500, 10000],
    unit: "IU",
    route: "subcutaneous",
  },
  fragmine: {
    doses: [2500, 5000, 7500, 10000],
    unit: "IU",
    route: "subcutaneous",
  },
  innohep: {
    doses: [2500, 3500, 4500, 10000, 14000, 18000],
    unit: "IU",
    route: "subcutaneous",
  },
  ozempic: { doses: [0.25, 0.5, 1, 2], unit: "mg", route: "subcutaneous" },
  wegovy: {
    doses: [0.25, 0.5, 1, 1.7, 2.4, 7.2],
    unit: "mg",
    route: "subcutaneous",
  },
  mounjaro: {
    doses: [2.5, 5, 7.5, 10, 12.5, 15],
    unit: "mg",
    route: "subcutaneous",
  },
  zepbound: {
    doses: [2.5, 5, 7.5, 10, 12.5, 15],
    unit: "mg",
    route: "subcutaneous",
  },
  saxenda: {
    doses: [0.6, 1.2, 1.8, 2.4, 3],
    unit: "mg",
    route: "subcutaneous",
  },
  victoza: { doses: [0.6, 1.2, 1.8], unit: "mg", route: "subcutaneous" },
  aspégic: { doses: [75, 100, 250], unit: "mg", route: "oral" },
  kardégic: { doses: [75, 160], unit: "mg", route: "oral" },
  glucophage: { doses: [500, 850, 1000], unit: "mg", route: "oral" },
  dostinex: { doses: [0.5, 1], unit: "mg", route: "oral" },
  levothyrox: {
    doses: [25, 50, 75, 100, 125, 150, 200],
    unit: "mcg",
    route: "oral",
  },
  euthyrox: {
    doses: [25, 50, 75, 100, 125, 150, 200],
    unit: "mcg",
    route: "oral",
  },
  cortancyl: { doses: [5, 10, 20], unit: "mg", route: "oral" },
  solupred: { doses: [5, 20], unit: "mg", route: "oral" },
  médrol: { doses: [4, 16, 32], unit: "mg", route: "oral" },
  vibramycine: { doses: [100], unit: "mg", route: "oral" },
};

function normalizePresetLookup(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]/g, "");
}

function getDosagePresetForMedicationName(
  medicationName: string,
): DosagePreset | null {
  const lower = medicationName.trim().toLowerCase();
  const exact = DOSAGE_PRESETS[lower];
  if (exact) return exact;

  const normalized = normalizePresetLookup(medicationName);
  const normalizedExact = Object.entries(DOSAGE_PRESETS).find(
    ([presetName]) => normalizePresetLookup(presetName) === normalized,
  )?.[1];
  if (normalizedExact) return normalizedExact;

  if (normalized.includes("ovidrel") || normalized.includes("ovitrelle")) {
    return DOSAGE_PRESETS.ovidrel ?? null;
  }
  if (normalized.includes("choriogonadotropinalfa")) {
    return DOSAGE_PRESETS["choriogonadotropin alfa"] ?? null;
  }
  if (normalized.includes("pregnyl")) return DOSAGE_PRESETS.pregnyl ?? null;
  if (normalized.includes("novarel")) return DOSAGE_PRESETS.novarel ?? null;
  if (
    normalized.includes("hcg") ||
    normalized.includes("chorionicgonadotropin") ||
    normalized.includes("choriogonadotropin")
  ) {
    return DOSAGE_PRESETS.hcg ?? null;
  }
  if (normalized.includes("lupron") || normalized.includes("leuprolide")) {
    return DOSAGE_PRESETS.lupron ?? null;
  }
  if (normalized.includes("decapeptyl") || normalized.includes("gonapeptyl")) {
    return DOSAGE_PRESETS.decapeptyl ?? null;
  }

  return null;
}

type DurationPreset = {
  labelKey: string;
  days: number | null; // null = ongoing
};

const DURATION_PRESETS: DurationPreset[] = [
  // `days` = number of days the med is taken, inclusive of startDate.
  // "Once" = 1 day → endDate === startDate.
  // "7 days" = 7 days → endDate === startDate + 6.
  { labelKey: "add_medication.duration_preset.once", days: 1 },
  { labelKey: "add_medication.duration_preset.7_days", days: 7 },
  { labelKey: "add_medication.duration_preset.10_days", days: 10 },
  { labelKey: "add_medication.duration_preset.14_days", days: 14 },
  { labelKey: "add_medication.duration_preset.3_weeks", days: 21 },
  { labelKey: "add_medication.duration_preset.1_month", days: 30 },
  { labelKey: "add_medication.duration_preset.3_months", days: 90 },
  { labelKey: "add_medication.duration_preset.ongoing", days: null },
];

/** Unified search result type covering all 3 tiers */
/**
 * One row in the name search.
 *
 * There is one source for these and it is the catalogue. The search used to
 * blend RxNorm, this app's content registry and a list of trigger-shot names
 * on top of the catalogue, which meant the rows a patient picked from were
 * mostly not things the server could store — and they still appeared when the
 * catalogue answered nothing at all.
 */
type MedSearchResult = {
  id: string;
  name: string;
  subtitle: string;
  /** The catalogue product this row is, which is what gets stored. */
  dbMed: DbMedication;
};

/** Known routes from the catalogue, never inferred from a code or dose form. */
function catalogRoutes(product: DbMedication): PatientMedicationRoute[] {
  return Array.from(
    new Set(
      (product.dimensions.routes ?? [])
        .map(serializeMedicationRoute)
        .filter((route): route is PatientMedicationRoute => route !== null),
    ),
  );
}

function getSearchResultRoutes(
  result: MedSearchResult,
): PatientMedicationRoute[] {
  return catalogRoutes(result.dbMed);
}

// ============================================================================
// Helpers
// ============================================================================

// getTodayString imported from @src/packages/utils/date as getToday

function mapRouteFromOCR(route: string | undefined): string {
  if (!route) return "";
  const knownRoute = deserializeMedicationRoute(route);
  if (ROUTES.includes(knownRoute)) return knownRoute;
  const lower = route.toLowerCase();
  if (lower.includes("oral")) return "oral";
  if (lower.includes("subcutaneous") || lower.includes("subq")) {
    return "subcutaneous";
  }
  if (lower.includes("intramuscular") || lower.includes("im")) {
    return "intramuscular";
  }
  if (lower.includes("vaginal")) return "vaginal";
  if (lower.includes("topical")) return "topical";
  if (lower.includes("patch")) return "patch";
  return "";
}

function mapTimeFromOCR(timeOfDay: string | undefined): string[] {
  if (!timeOfDay) return [];
  const lower = timeOfDay.toLowerCase();
  const times: string[] = [];
  if (lower.includes("morning")) times.push("08:00");
  if (lower.includes("afternoon") || lower.includes("noon")) {
    times.push("12:00");
  }
  if (lower.includes("evening")) times.push("18:00");
  if (lower.includes("night") || lower.includes("bedtime")) times.push("21:00");
  return times;
}

function addDaysToLocalDate(dateStr: string, days: number): string {
  const d = new Date(dateStr + "T00:00:00");
  d.setDate(d.getDate() + days);
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function computeEndDate(startDate: string, days: number): string {
  // `days` is the number of days the medication is taken, *including* the
  // start date. So a 7-day course beginning June 1 ends on June 7, not
  // June 8 — the calendar filter treats endDate as inclusive (med shown
  // on the endDate itself). For "Once" (days === 1), endDate === startDate.
  const offset = Math.max(0, days - 1);
  const d = new Date(startDate + "T00:00:00");
  d.setDate(d.getDate() + offset);
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function formatTimeSummary(
  times: string[],
  t: (key: string) => string,
): string {
  if (times.length === 0) return t("add_medication.no_reminders_set");
  const labels = times.map((time) => {
    const preset = TIME_PRESETS.find((p) => p.value === time);
    return preset ? t(preset.labelKey) : time;
  });
  return `${times.length}× ${labels.join(" & ")}`;
}

function formatDoseLabel(amount: string, unit: string): string {
  const trimmedAmount = amount.trim();
  const trimmedUnit = unit.trim();
  if (!trimmedAmount && !trimmedUnit) return "";
  return [trimmedAmount, trimmedUnit].filter(Boolean).join(" ");
}

function getRouteLabel(route: string, t: (key: string) => string): string {
  const option = ROUTE_OPTIONS.find((item) => item.value === route);
  return option ? t(option.labelKey) : route;
}

function getDeliveryMethodLabel(
  deliveryMethod: string,
  t: (key: string) => string,
): string | null {
  const option = INJECTION_DELIVERY_METHODS.find(
    (item) => item.value === deliveryMethod,
  );
  return option ? t(option.labelKey) : null;
}

function inferInventoryForm(
  route: string,
  deliveryMethod: string,
): AddMedicationInventoryForm {
  if (deliveryMethod === "pre_filled_pen") return "pen";
  if (
    deliveryMethod === "injection_solution" ||
    deliveryMethod === "lyophilized_powder" ||
    deliveryMethod === "multi_vial"
  ) {
    return "vial";
  }
  if (route === "vaginal") return "suppository";
  if (route === "oral") return "pill";
  return "supplies";
}

function inferInventoryUnitLabel(
  route: string,
  deliveryMethod: string,
): string {
  if (deliveryMethod === "pre_filled_pen") return "pens";
  if (
    deliveryMethod === "injection_solution" ||
    deliveryMethod === "lyophilized_powder" ||
    deliveryMethod === "multi_vial"
  ) {
    return "vials";
  }
  if (route === "vaginal") return "inserts";
  if (route === "oral") return "pills";
  return "units";
}

/**
 * A menu opened from inside a field after its value, such as the route after
 * a name or the unit after a dose. A hairline parts it from the value; the
 * field's own border marks the field, so the menu adds none of its own.
 */
const fieldMenuTriggerStyle = {
  gap: 4,
  minHeight: 32,
  paddingLeft: 10,
  paddingRight: 6,
  borderLeftWidth: 1,
  borderLeftColor: semanticColors.border.subtle,
};

/** A row holding a switch, on the same surface as the fields around it. */
const toggleRowStyle = {
  paddingVertical: 12,
  paddingHorizontal: 14,
  borderRadius: radii.md,
  borderWidth: 1,
  borderColor: semanticColors.border.subtle,
  backgroundColor: semanticColors.surface.input,
  marginBottom: 16,
};

/** Every unit stock is counted in, as the inventory stores it. */
const INVENTORY_UNITS = ["pens", "vials", "units", "pills", "inserts"];

/**
 * The units a medication's stock can be counted in, by how it is taken. An
 * injectable comes as pens or vials, or as single units such as syringes.
 */
function inventoryUnitsForRoute(route: string): string[] {
  if (INJECTABLE_ROUTES.includes(route)) return ["pens", "vials", "units"];
  if (route === "vaginal") return ["inserts"];
  if (route === "oral") return ["pills"];
  return ["units"];
}

/** Pre-fill form fields from a content registry medication or supplement block */

// ============================================================================
// Medication illustrations shared with the Care medication rows
// ============================================================================

// The frosted Dialog already supplies the blur. A second backdrop filter
// inside it washes these small circles white; retain the shared glass fill,
// rim and lift while letting the dialog's material show through.
const inheritedDialogBlurStyle =
  Platform.OS === "web"
    ? {
        backdropFilter: "none",
        WebkitBackdropFilter: "none",
      }
    : {};

const medicationHeaderCircleSurfaceStyle = {
  ...compactSecondarySurfaceStyle,
  ...inheritedDialogBlurStyle,
};

function medicationIllustration(medicationName: string, size = 32) {
  const form = getMedicationByBrandName(medicationName)?.body.commonForms[0];
  if (
    form === "pen" ||
    form === "vial" ||
    form === "pill" ||
    form === "suppository"
  ) {
    return <MedFormIcon form={form} size={size} />;
  }
  return <Pill size={size * 0.65} color={EMBER.colors.textLight} />;
}

/** Existing Care artwork, selected from the confirmed form or known medication. */
function medicationHeaderArtwork(medicationName: string, deliveryMethod = "") {
  const form = serializeMedicationForm(deliveryMethod);
  switch (form) {
    case "pre_filled_pen":
      return { kind: "pen", source: injectionPenArtwork };
    case "injection_solution":
    case "lyophilized_powder":
    case "pre_filled_syringe":
      return { kind: "vial", source: vialSyringeArtwork };
    case "tablet":
    case "capsule":
    case "sublingual_tablet":
    case "vaginal_suppository":
      return { kind: "pill", source: pillCapsuleArtwork };
  }

  // Pending OCR items can still carry a dose/delivery suffix in their names.
  // This only picks artwork; the original processing claims remain untouched.
  const brandName = medicationName
    .replace(/\s*\((stylo|pen|seringue|syringe|poudre|powder)\)\s*/i, " ")
    .replace(
      /\s+\d+(?:[.,]\d+)?\s*(mg|iu|mcg|ml|g|units?|pills?|tablets?|capsules?|drops?|patches?)\b.*$/i,
      "",
    )
    .trim();
  const knownForm = getMedicationByBrandName(brandName)?.body.commonForms[0];
  if (knownForm === "pen") return { kind: "pen", source: injectionPenArtwork };
  if (knownForm === "vial") return { kind: "vial", source: vialSyringeArtwork };
  return { kind: "pill", source: pillCapsuleArtwork };
}

// ============================================================================
// Main Component
// ============================================================================

function prescriptionFormFields(med: DocumentProcessingPrefillMedication) {
  const TRAILING_DOSE =
    /^(.+?)\s+(\d+(?:[.,]\d+)?)\s*(mg|iu|mcg|ml|g|units?|pills?|tablets?|capsules?|drops?|patches?)\b\.?$/i;
  const PAREN_DELIVERY =
    /\s*\((stylo|pen|seringue|syringe|poudre|powder)\)\s*/i;

  let rawName = (med.medicationName ?? "").trim();
  let parsedDelivery: string | null = null;

  const delivMatch = rawName.match(PAREN_DELIVERY);
  if (delivMatch) {
    const kw = delivMatch[1]!.toLowerCase();
    if (kw === "stylo" || kw === "pen") parsedDelivery = "pre_filled_pen";
    else if (kw === "seringue" || kw === "syringe") {
      parsedDelivery = "pre_filled_syringe";
    } else if (kw === "poudre" || kw === "powder") {
      parsedDelivery = "lyophilized_powder";
    }
    rawName = rawName.replace(PAREN_DELIVERY, " ").trim();
  }

  const doseMatch = rawName.match(TRAILING_DOSE);
  if (doseMatch) {
    rawName = doseMatch[1]!.trim();
  }

  const unit = med.dosageUnit.trim().toLowerCase().replace(/^ui$/, "iu");
  return {
    name: rawName,
    dosageAmount: med.dosage,
    dosageUnit: DOSE_UNITS.find((u) => unit.includes(u.toLowerCase())) ?? "",
    route: mapRouteFromOCR(med.route),
    deliveryMethod:
      serializeMedicationForm(med.deliveryMethod) || parsedDelivery || "",
    selectedTimes: mapTimeFromOCR(med.timeOfDay),
    notes: med.instructions,
  };
}

export function AddMedicationModal({
  visible,
  onClose,
  cycleId,
  initialStartDate,
  menstruationStartDate,
  cycleDateBounds,
  phaseId,
  onRequestRetrievalAppointment,
  medicationRole: externalMedicationRole,
  editMedication,
  prefillMedication,
  inventoryMode = false,
  onInventoryMedicationAdded,
  onMedicationCreated,
  onMedicationUpdated,
  replacingMedicationName,
}: Props) {
  const capturePress = usePressCapture("AddMedicationModal");
  const { height: windowHeight } = useWindowDimensions();
  const timezone = useTimezone();
  const { format: formatDate } = useDateFormat();
  const t = useTranslations();
  const { showToast } = useToast();
  const { client, patientId } = useApiClient();
  const { countryCode: patientCountryCode } = useUserCountry();
  // What the catalogue suggests adding first in her market, rather than a
  // list of brand names this app carries. A market whose entries the
  // catalogue cannot resolve to a product answers with searches instead, and
  // those chips fill the box rather than skipping ahead to the form.
  const { data: quickAddItems = [], isPending: isQuickAddPending } =
    useMedicationQuickAdd(patientCountryCode);
  const routeMenuItems = useMemo<AnchoredSelectMenuItem[]>(
    () => [
      ...ROUTE_OPTIONS.map((option) => ({
        value: option.value,
        label: t(option.labelKey),
        icon: option.icon,
      })),
      {
        value: "",
        label: t("add_medication.route_option.not_specified"),
        icon: X,
        muted: true,
        separatorBefore: true,
      },
    ],
    [t],
  );
  const doseUnitMenuItems = useMemo<AnchoredSelectMenuItem[]>(
    () =>
      DOSE_UNITS.map((unit) => ({
        value: unit,
        label: DOSE_UNIT_KEYS[unit] ? t(DOSE_UNIT_KEYS[unit]) : unit,
      })),
    [t],
  );

  // Step state
  const [step, setStep] = useState<Step>(0);

  // Form state
  const [name, setName] = useState("");
  // What she is typing, which is not what she has chosen. Searching on `name`
  // meant picking a result re-ran the search for the name it had just set,
  // and the form waited on a request it had no use for.
  const [searchTerm, setSearchTerm] = useState("");
  // What the pack is, as she answers for it. The server resolves the pack
  // from these values; the client never picks one itself.
  const [identity, setIdentity] = useState<MedicationIdentityDraft>(() =>
    createMedicationIdentityDraft(null),
  );
  // The routes the packs still consistent with her answers are given by.
  const [packRoutes, setPackRoutes] = useState<string[]>([]);
  // The route this screen filled in by itself, so that narrowing the pack can
  // withdraw its own answer without ever touching one she chose.
  const settledRouteRef = useRef<string | null>(null);
  const [medicationSelected, setMedicationSelected] = useState(false);
  const [scanPanelOpen, setScanPanelOpen] = useState(false);
  const [packIsTriggerCandidate, setPackIsTriggerCandidate] = useState(false);
  const [startDate, setStartDate] = useState(() => initialStartDate ?? "");
  const [endDate, setEndDate] = useState("");
  const [isStartDateInputValid, setIsStartDateInputValid] = useState(true);
  const [startDateInputSession, setStartDateInputSession] = useState(0);
  const [confirmingStartDate, setConfirmingStartDate] = useState(false);
  const configureScrollRef = useRef<ScrollView>(null);
  const [dosageAmount, setDosageAmount] = useState("");
  const [doseError, setDoseError] = useState<
    "invalid" | "not_positive" | "too_large" | null
  >(null);
  const [dosageUnit, setDosageUnit] = useState("");
  const [showDoseUnitDropdown, setShowDoseUnitDropdown] = useState(false);
  const doseUnitTriggerRef = useRef<View>(null);
  const [route, setRoute] = useState("");
  const [deliveryMethod, setDeliveryMethod] = useState("");
  const [showRouteDropdown, setShowRouteDropdown] = useState(false);
  const routeTriggerRef = useRef<View>(null);
  const [selectedTimes, setSelectedTimes] = useState<string[]>([]);
  const [showCustomTimeInput, setShowCustomTimeInput] = useState(false);
  const [customTimeValue, setCustomTimeValue] = useState("");
  const [notes, setNotes] = useState("");
  const [linkToCycle, setLinkToCycle] = useState(true);
  const [inventoryQuantity, setInventoryQuantity] = useState("1");
  const [inventoryUnitLabel, setInventoryUnitLabel] = useState("");
  const inventoryUnitPicked = useRef(false);
  const [includeTakingSchedule, setIncludeTakingSchedule] = useState(false);

  // Duplicate-detection modal — fires when the entered medication name
  // matches an active patient medication AND a scheduled time overlaps.
  // #3707: a doublon is how patients end up with two patientMedicationIds
  // that share a display name, which was the trigger for the dose-matching
  // fallthrough bug.
  const [duplicateMatch, setDuplicateMatch] = useState<{
    medicationId: string;
    name: string;
    time: string;
    notes: string | null;
  } | null>(null);

  // Duration state
  const [durationPreset, setDurationPreset] = useState<string | null>(null);
  const [customDurationValue, setCustomDurationValue] = useState("");
  const [customDurationUnit, setCustomDurationUnit] = useState<
    "days" | "weeks" | "months"
  >("days");

  // Database medication linkage
  const [selectedMedicationDbId, setSelectedMedicationDbId] = useState<
    string | null
  >(null);
  const [selectedDbMed, setSelectedDbMed] = useState<DbMedication | null>(null);

  // Trigger shot state
  const [isTriggerShot, setIsTriggerShot] = useState(false);

  // The prescription recap confirms all courses together; individual forms
  // only edit unsaved drafts.
  const [ocrMedQueue, setOcrMedQueue] = useState<
    DocumentProcessingPrefillMedication[]
  >([]);
  const [ocrMedIndex, setOcrMedIndex] = useState(0);
  const [scanError, setScanError] = useState<
    | "photo_access_needed"
    | "no_medications_found"
    | "prescription_unreadable"
    | null
  >(null);
  const [unreadableScan, setUnreadableScan] = useState<{
    file: DocumentSourceFile;
    uploadId?: string;
  } | null>(null);
  const [saveError, setSaveError] = useState<
    | { kind: "date_outside_cycle" }
    | { kind: "save_failed"; retry: "submit" | "submit_and_add_another" }
    // The medication saved but its stock didn't; Try again redoes the stock.
    | { kind: "stock_not_added" }
    | null
  >(null);
  const clearScanError = useCallback(() => {
    setScanError(null);
    setUnreadableScan(null);
  }, []);
  const [savedPrescriptionIndices, setSavedPrescriptionIndices] = useState<
    number[]
  >([]);

  const [isSavingPrescription, setIsSavingPrescription] = useState(false);
  const [prescriptionError, setPrescriptionError] = useState("");
  // Only a failed write can be tried again; the other prescription errors
  // ask her to fix a detail first.
  const [prescriptionAddFailed, setPrescriptionAddFailed] = useState(false);
  const prescriptionSaveInFlight = useRef(false);
  const triggerTimingWarning = useRef(false);
  // Track each successful write immediately, including a medication whose
  // inventory callback later fails. Retrying must not create that course twice.
  const createdPrescriptionIndices = useRef(new Set<number>());
  const stockedPrescriptionIndices = useRef(new Set<number>());
  // The same for the single medication this dialog adds: once created, a
  // retry after its stock failed redoes only the stock.
  const createdSingleMedication = useRef(false);

  // These are unsaved UI drafts, not a second medication data source. Keep
  // every field when the patient returns to the recap or chooses another row.
  const prescriptionDraft = useMemo(
    () => ({
      name,
      dosageAmount,
      dosageUnit,
      route,
      deliveryMethod,
      startDate,
      endDate,
      selectedTimes,
      showCustomTimeInput,
      customTimeValue,
      notes,
      linkToCycle,
      durationPreset,
      customDurationValue,
      customDurationUnit,
      selectedMedicationDbId,
      selectedDbMed,
      isTriggerShot,
      inventoryQuantity,
      inventoryUnitLabel,
      inventoryUnitPicked: inventoryUnitPicked.current,
      includeTakingSchedule,
    }),
    [
      name,
      dosageAmount,
      dosageUnit,
      route,
      deliveryMethod,
      startDate,
      endDate,
      selectedTimes,
      showCustomTimeInput,
      customTimeValue,
      notes,
      linkToCycle,
      durationPreset,
      customDurationValue,
      customDurationUnit,
      selectedMedicationDbId,
      selectedDbMed,
      isTriggerShot,
      inventoryQuantity,
      inventoryUnitLabel,
      includeTakingSchedule,
    ],
  );
  const prescriptionDrafts = useRef<Map<number, typeof prescriptionDraft>>(
    new Map(),
  );

  const rememberPrescriptionDraft = useCallback(() => {
    prescriptionDrafts.current.set(ocrMedIndex, prescriptionDraft);
  }, [ocrMedIndex, prescriptionDraft]);

  // Quick add and prescription prefill set the name before its catalog query
  // completes. Link an exact match once it arrives, preserving an explicit
  // search/pack selection and every dose or route the patient has entered.
  const {
    data: dbMedications = [],
    isFetching: isCatalogFetching,
    isSettling: isCatalogSettling,
  } = useMedicationProductVariations(searchTerm, patientCountryCode);
  // Idle is not loading. `isPending` is true for a query that has never run —
  // which, while the box is empty and the query disabled, is for ever, and the
  // search input spun the whole time.
  const isCatalogLoading = isCatalogFetching || isCatalogSettling;
  const isSearchActive = searchTerm.trim().length >= SEARCH_MIN_LENGTH;
  // The draft follows the name, and naming a different medication clears the
  // pack answers that described the old one.
  useEffect(() => {
    setIdentity((current) =>
      current.productName === name ? current : withProductName(current, name),
    );
  }, [name]);
  useEffect(() => {
    setIdentity((current) =>
      current.countryCode === patientCountryCode
        ? current
        : { ...current, countryCode: patientCountryCode },
    );
  }, [patientCountryCode]);

  // What the identity section submits. Editing never re-identifies the
  // medication, so it travels only on create.
  const submittedIdentity = useMemo(
    () => (editMedication ? null : buildMedicationIdentityInput(identity)),
    [identity, editMedication],
  );

  // One reachable route is an answer, not a choice: it is filled in. More
  // than one, or none, withdraws an answer this screen gave — never one she
  // gave herself.
  useEffect(() => {
    const isOurs = !route || route === settledRouteRef.current;
    if (!isOurs) return;
    if (packRoutes.length === 1) {
      const only = deserializeMedicationRoute(packRoutes[0]);
      if (ROUTES.includes(only) && route !== only) {
        settledRouteRef.current = only;
        setRoute(only);
      }
      return;
    }
    if (route && route === settledRouteRef.current) {
      settledRouteRef.current = null;
      setRoute("");
    }
  }, [packRoutes, route]);

  // The routes on offer: what the chosen packs allow, or everything when no
  // pack has been described yet.
  const routeChoices = useMemo(() => {
    const reachable = packRoutes
      .map((route) => deserializeMedicationRoute(route))
      .filter((route) => ROUTES.includes(route));
    if (reachable.length === 0) return ROUTE_OPTIONS;
    return ROUTE_OPTIONS.filter((option) => reachable.includes(option.value));
  }, [packRoutes]);
  const catalogMatch = findMedicationByName(dbMedications, name);
  const resolvedMedicationDbId = resolveMedicationCatalogId(
    selectedMedicationDbId,
    dbMedications,
    name,
  );
  const catalogMedication = selectedDbMed ?? catalogMatch;
  const showTriggerToggle =
    packIsTriggerCandidate ||
    catalogMedication?.therapeuticClass === "hcg" ||
    catalogMedication?.defaultRole === "trigger_shot";
  const isTriggerShotContext = externalMedicationRole === "trigger_shot";
  const effectiveIsTriggerShot = isTriggerShotContext || isTriggerShot;

  // Mutations
  const createMedication = useCreateMedication();
  const updateMedication = useUpdateMedication();
  const updatePhase = useUpdatePhase();
  const { data: patientMedications = [] } = usePatientMedications({
    enabled: !editMedication,
  });
  // The durable home of trigger-shot intent is phase.triggerShot on the
  // protocol phase (#2224) — phases are cache-shared with the cycle screens.
  const { data: cyclePhases = [] } = usePhases(cycleId || null);
  const isEditMode = !!editMedication;

  // The source is saved before canonical processing starts. The resulting run
  // is used only to prefill this form; the complete claims/evidence remain on
  // the durable document-processing record for every other consumer.
  const transfer = useDocumentSourceTransfer();
  const resetTransfer = transfer.reset;
  const prescriptionScanGeneration = useRef(0);
  // A delayed scan cannot overwrite a medication chosen while it was running.
  useEffect(
    () => () => {
      prescriptionScanGeneration.current += 1;
    },
    [visible, step, name],
  );
  const isExtracting =
    transfer.state === "uploading" ||
    transfer.state === "uploaded" ||
    transfer.state === "filing";

  // Apply the first scanned medication to the active form. Recap drafts use
  // the same normalization when an individual course is opened for editing.
  //
  // Printed product names can include strength and delivery details. Clean the
  // display name while taking the prescribed dose from the canonical prefill.
  const applyOcrMedToForm = useCallback(
    (med: DocumentProcessingPrefillMedication) => {
      const fields = prescriptionFormFields(med);
      setName(fields.name);
      setMedicationSelected(!!fields.name);
      setDosageAmount(fields.dosageAmount);
      setDosageUnit(fields.dosageUnit);
      setRoute(fields.route);
      setDeliveryMethod(fields.deliveryMethod);
      setSelectedTimes(fields.selectedTimes);
      setNotes(fields.notes);
      if (med.startDate) setStartDate(med.startDate);
      if (med.endDate) setEndDate(med.endDate);
    },
    [],
  );

  // Clear the per-medication fields without touching the OCR queue, so we can
  // walk to the next medication in a multi-med prescription.
  const clearMedicationFields = useCallback(() => {
    setPackIsTriggerCandidate(false);
    setName("");
    setMedicationSelected(false);
    setDosageAmount("");
    setDosageUnit("");
    setRoute("");
    setDeliveryMethod("");
    setSelectedTimes([]);
    setEndDate("");
    setDurationPreset(null);
    setCustomDurationValue("");
    setCustomDurationUnit("days");
    setNotes("");
    setSelectedMedicationDbId(null);
    setSelectedDbMed(null);
    setDoseError(null);
    setIsStartDateInputValid(true);
    setShowDoseUnitDropdown(false);
    setShowRouteDropdown(false);
    setShowCustomTimeInput(false);
    setCustomTimeValue("");
    setIsTriggerShot(false);
  }, []);

  const makePrescriptionDraft = useCallback(
    (med: DocumentProcessingPrefillMedication) => {
      const draft: typeof prescriptionDraft = {
        ...prescriptionFormFields(med),
        // A selected calendar day, cycle date, or upload date is not a
        // prescribed start. Keep an absent date unresolved on the recap.
        startDate: med.startDate,
        endDate: med.endDate,
        showCustomTimeInput: false,
        customTimeValue: "",
        linkToCycle: true,
        durationPreset: null,
        customDurationValue: "",
        customDurationUnit: "days",
        selectedMedicationDbId: null,
        selectedDbMed: null,
        isTriggerShot: false,
        inventoryQuantity: "1",
        inventoryUnitLabel: "",
        inventoryUnitPicked: false,
        includeTakingSchedule: false,
      };
      return draft;
    },
    [],
  );

  function getPrescriptionDraft(index: number) {
    return (
      prescriptionDrafts.current.get(index) ??
      makePrescriptionDraft(ocrMedQueue[index]!)
    );
  }

  function prescriptionEndDate(draft: typeof prescriptionDraft) {
    if (!draft.startDate) return draft.endDate || null;
    if (!draft.durationPreset) return draft.endDate || null;
    const preset = DURATION_PRESETS.find(
      (p) => p.labelKey === draft.durationPreset,
    );
    if (preset?.days != null) {
      return computeEndDate(draft.startDate, preset.days);
    }
    if (draft.durationPreset === "Custom" && draft.customDurationValue) {
      const days = Number.parseInt(draft.customDurationValue, 10);
      const multiplier =
        draft.customDurationUnit === "weeks"
          ? 7
          : draft.customDurationUnit === "months"
            ? 30
            : 1;
      return days > 0
        ? computeEndDate(draft.startDate, days * multiplier)
        : null;
    }
    return null;
  }

  function openPrescriptionMedication(index: number, confirmStartDate = false) {
    const med = ocrMedQueue[index];
    if (
      !med ||
      createdPrescriptionIndices.current.has(index) ||
      stockedPrescriptionIndices.current.has(index) ||
      prescriptionSaveInFlight.current
    ) {
      return;
    }
    clearMedicationFields();
    setConfirmingStartDate(confirmStartDate);
    setOcrMedIndex(index);
    const draft = getPrescriptionDraft(index);
    setName(draft.name);
    setMedicationSelected(true);
    setDosageAmount(draft.dosageAmount);
    setDosageUnit(draft.dosageUnit);
    setRoute(draft.route);
    setDeliveryMethod(draft.deliveryMethod);
    setStartDate(draft.startDate);
    setEndDate(draft.endDate);
    setSelectedTimes(draft.selectedTimes);
    setShowCustomTimeInput(draft.showCustomTimeInput);
    setCustomTimeValue(draft.customTimeValue);
    setNotes(draft.notes);
    setLinkToCycle(draft.linkToCycle);
    setDurationPreset(draft.durationPreset);
    setCustomDurationValue(draft.customDurationValue);
    setCustomDurationUnit(draft.customDurationUnit);
    setSelectedMedicationDbId(draft.selectedMedicationDbId);
    setSelectedDbMed(draft.selectedDbMed);
    setIsTriggerShot(draft.isTriggerShot);
    setInventoryQuantity(draft.inventoryQuantity);
    setInventoryUnitLabel(draft.inventoryUnitLabel);
    inventoryUnitPicked.current = draft.inventoryUnitPicked;
    setIncludeTakingSchedule(draft.includeTakingSchedule);
    setStep(2);
  }

  // Reset all state
  const resetState = useCallback(() => {
    prescriptionScanGeneration.current += 1;
    setScanPanelOpen(false);
    setPackIsTriggerCandidate(false);
    setStep(0);
    setName("");
    setMedicationSelected(false);
    setStartDate(initialStartDate ?? "");
    setEndDate("");
    setIsStartDateInputValid(true);
    setConfirmingStartDate(false);
    setDosageAmount("");
    setDosageUnit("");
    setShowDoseUnitDropdown(false);
    setRoute("");
    setDeliveryMethod("");
    setShowRouteDropdown(false);
    setSelectedTimes([]);
    setShowCustomTimeInput(false);
    setCustomTimeValue("");
    setNotes("");
    setLinkToCycle(true);
    setInventoryQuantity("1");
    setInventoryUnitLabel("");
    inventoryUnitPicked.current = false;
    setIncludeTakingSchedule(false);
    setDurationPreset(null);
    setCustomDurationValue("");
    setCustomDurationUnit("days");
    setSelectedMedicationDbId(null);
    setSelectedDbMed(null);
    setIsTriggerShot(false);
    setOcrMedQueue([]);
    setOcrMedIndex(0);
    setSavedPrescriptionIndices([]);
    createdPrescriptionIndices.current.clear();
    stockedPrescriptionIndices.current.clear();
    createdSingleMedication.current = false;
    setPrescriptionError("");
    setPrescriptionAddFailed(false);
    prescriptionDrafts.current.clear();
    clearScanError();
    setSaveError(null);
    resetTransfer();
  }, [clearScanError, initialStartDate, resetTransfer]);

  const handleClose = useCallback(() => {
    if (prescriptionSaveInFlight.current) return;
    const hasTriggerTimingWarning = triggerTimingWarning.current;
    triggerTimingWarning.current = false;
    resetState();
    onClose();
    if (hasTriggerTimingWarning) {
      // A batch may keep saving after its trigger phase failed. Wait for this
      // dialog to close so the warning remains visible at the app root.
      setTimeout(() => {
        showToast({
          variant: "error",
          message: t("add_medication.alert_trigger_phase_failed_message"),
        });
      }, 350);
    }
  }, [onClose, resetState, showToast, t]);

  const dismissSelectionMenus = useCallback(() => {
    setShowRouteDropdown(false);
    setShowDoseUnitDropdown(false);
  }, []);

  // Pre-fill form when entering edit mode
  useEffect(() => {
    if (visible && editMedication) {
      setName(editMedication.name);
      if (editMedication.dose != null) {
        setDosageAmount(String(editMedication.dose));
      }
      if (editMedication.doseUnit) setDosageUnit(editMedication.doseUnit);
      setRoute(deserializeMedicationRoute(editMedication.route));
      if (editMedication.form) setDeliveryMethod(editMedication.form);
      if (editMedication.timeTaken && editMedication.timeTaken.length > 0) {
        setSelectedTimes(editMedication.timeTaken);
      }
      setStartDate(editMedication.startDate ?? "");
      if (editMedication.endDate) setEndDate(editMedication.endDate);
      if (editMedication.notes) setNotes(editMedication.notes);
      setStep(2); // Go straight to Configure
    }
  }, [visible, editMedication]);

  // Pre-fill form when launched from an extracted-tag context (new medication,
  // not an edit). Fields the patient still has to pick (route, delivery, times)
  // are left for them.
  useEffect(() => {
    if (visible && prefillMedication && !editMedication) {
      setName(prefillMedication.name);
      if (prefillMedication.dose != null) {
        setDosageAmount(String(prefillMedication.dose));
      }
      if (prefillMedication.doseUnit) {
        setDosageUnit(prefillMedication.doseUnit);
      }
      if (prefillMedication.startDate) {
        setStartDate(prefillMedication.startDate);
      }
      if (prefillMedication.notes) setNotes(prefillMedication.notes);
      setStep(2); // Skip search; land on Configure.
    }
  }, [visible, prefillMedication, editMedication]);

  useEffect(() => {
    if (visible && !editMedication) {
      setStartDate(initialStartDate ?? "");
    }
  }, [editMedication, initialStartDate, visible]);

  useEffect(() => {
    if (visible && !editMedication && isTriggerShotContext) {
      setIsTriggerShot(true);
    }
  }, [visible, editMedication, isTriggerShotContext]);

  // Clear delivery method when route changes to non-injectable
  const handleRouteChange = useCallback(
    (newRoute: string) => {
      setRoute(newRoute);
      const changedToNonInjectable =
        newRoute !== "" && !INJECTABLE_ROUTES.includes(newRoute);
      const nextDeliveryMethod = changedToNonInjectable ? "" : deliveryMethod;
      if (changedToNonInjectable) {
        setDeliveryMethod("");
      }
      if (inventoryMode && !inventoryUnitLabel.trim()) {
        setInventoryUnitLabel(
          inferInventoryUnitLabel(newRoute, nextDeliveryMethod),
        );
      }
    },
    [deliveryMethod, inventoryMode, inventoryUnitLabel],
  );

  // The stock unit follows the route and delivery method (a pen injector
  // counts pens) until she picks a unit herself.
  useEffect(() => {
    if (!inventoryMode || inventoryUnitPicked.current) return;
    setInventoryUnitLabel(inferInventoryUnitLabel(route, deliveryMethod));
  }, [deliveryMethod, inventoryMode, route]);

  // Time toggle
  const handleTimeToggle = useCallback((value: string) => {
    setSelectedTimes((prev) =>
      prev.includes(value) ? prev.filter((t) => t !== value) : [...prev, value],
    );
  }, []);

  // Save and process the prescription source before using it to prefill the
  // medication form. The server keeps every claim, segment, party, and piece
  // of evidence even when this form only consumes the medication subset.
  const readPrescription = useCallback(
    async (file: DocumentSourceFile, uploadId?: string) => {
      const generation = ++prescriptionScanGeneration.current;
      let uploadedId = uploadId;
      try {
        const source = uploadId
          ? { file, uploadId }
          : await transfer.uploadSource(file, {
              destination: "medication",
              sourceContext: {
                surface: "add_medication_modal",
                route: "AddMedicationModal",
                form: "medication",
                allowBackgroundProcessing: true,
              },
            });
        uploadedId = source.uploadId;
        // Prescription extraction regularly outlasts the shared transfer's
        // short foreground poll. Keep this dialog loading for up to 150 seconds
        // so a successful scan can prefill the form before the user reviews it.
        const outcome = await transfer.processSource(source, {
          attempts: 120,
          intervalMs: 1_250,
        });
        if (generation !== prescriptionScanGeneration.current) return;
        const projected = projectDocumentProcessingRunToPrefill(
          outcome.run,
          file.name,
        );
        const meds = projected.medications ?? [];
        setUnreadableScan(null);
        setOcrMedQueue(meds);
        setOcrMedIndex(0);
        setSavedPrescriptionIndices([]);
        createdPrescriptionIndices.current.clear();
        stockedPrescriptionIndices.current.clear();
        setPrescriptionError("");
        setPrescriptionAddFailed(false);
        prescriptionDrafts.current.clear();
        meds.forEach((med, index) =>
          prescriptionDrafts.current.set(index, makePrescriptionDraft(med)),
        );
        if (meds.length > 0) {
          setScanError(null);
          applyOcrMedToForm(meds[0]!);
        } else {
          setScanError("no_medications_found");
        }
        setStartDate(meds[0]?.startDate ?? "");
        setIsStartDateInputValid(true);
        setStep(meds.length > 0 ? 1 : 2);
      } catch (error) {
        if (generation !== prescriptionScanGeneration.current) return;
        console.error(
          "[AddMedicationModal] canonical processing error:",
          error,
        );
        setScanError("prescription_unreadable");
        setUnreadableScan({ file, uploadId: uploadedId });
        setStep(2);
      }
    },
    [applyOcrMedToForm, makePrescriptionDraft, transfer],
  );

  const handleScanPrescription = useCallback(async () => {
    const generation = ++prescriptionScanGeneration.current;
    clearScanError();
    const permResult = await ImagePicker.requestMediaLibraryPermissionsAsync();
    if (generation !== prescriptionScanGeneration.current) return;
    if (!permResult.granted) {
      setScanError("photo_access_needed");
      return;
    }
    const result = await ImagePicker.launchImageLibraryAsync({
      mediaTypes: ["images"],
      quality: 0.8,
    });
    if (result.canceled || !result.assets || result.assets.length === 0) return;
    if (generation !== prescriptionScanGeneration.current) return;
    const asset = result.assets[0]!;
    const file: DocumentSourceFile = {
      uri: asset.uri,
      name: asset.fileName || "prescription.jpg",
      mimeType: resolveUploadContentType({
        name: asset.fileName || "prescription.jpg",
        uri: asset.uri,
        mimeType: asset.mimeType,
      }),
    };
    await readPrescription(file);
  }, [clearScanError, readPrescription]);

  // ── 3-tier medication search ──────────────────────────────────────
  // Tier 1: Backend catalog (real UUIDs)
  // Tier 2: RxNorm API (comprehensive FDA drugs)
  // Tier 3: Content registry (supplements + educational)

  // The catalogue's answer, as rows. No tier above it and none below: a name
  // this app cannot store is not a name worth offering.
  const searchResults = useMemo(
    (): MedSearchResult[] =>
      dbMedications.map((product) => ({
        id: product.medicationId,
        name: product.productName,
        subtitle: product.ingredients.join(" · "),
        dbMed: product,
      })),
    [dbMedications],
  );

  /** Handle selecting a unified search result */
  const handleSelectResult = useCallback((result: MedSearchResult) => {
    setPackIsTriggerCandidate(false);
    setMedicationSelected(true);
    setDosageAmount("");
    setDosageUnit("");
    setRoute("");
    setDeliveryMethod("");
    setSelectedMedicationDbId(null);
    setSelectedDbMed(null);
    setIsTriggerShot(false);
    setDoseError(null);
    setName(result.name);

    // Her dose suggestion for this medication. The route is not taken from
    // here: the catalogue says which routes the product's packs are given
    // by, and that is the better answer.
    const presets = getDosagePresetForMedicationName(result.name);
    if (presets) setDosageUnit(presets.unit);
    // Only the brand is carried over: the dose form, strength, container
    // and pack size are hers to pick from the variations this product
    // actually has, and filling them in here would choose a pack for her.
    setIdentity((current) => ({
      ...withProductName(current, result.name),
      brand: result.dbMed.brand,
    }));

    // The row is the catalogue product, so there is nothing to look back up:
    // its id, its forms and its role come with it.
    setSelectedMedicationDbId(result.dbMed.medicationId);
    setSelectedDbMed(result.dbMed);
    const forms = result.dbMed.dimensions.doseForms
      .map(serializeMedicationForm)
      .filter((form) => form !== null);
    if (forms.length === 1) setDeliveryMethod(forms[0]!);
    if (result.dbMed.defaultRole === "trigger_shot") setIsTriggerShot(true);

    const knownRoutes = getSearchResultRoutes(result);
    if (knownRoutes.length === 1) {
      setRoute(deserializeMedicationRoute(knownRoutes[0]));
    }

    setStep(2);
  }, []);

  // Quick add
  const handleQuickAdd = useCallback(
    (medName: string) => {
      setPackIsTriggerCandidate(false);
      setMedicationSelected(true);
      setDosageAmount("");
      setDosageUnit("");
      setRoute("");
      setDeliveryMethod("");
      setSelectedMedicationDbId(null);
      setSelectedDbMed(null);
      setIsTriggerShot(false);
      setDoseError(null);
      const dbMatch = findMedicationByName(dbMedications, medName);
      const presets = getDosagePresetForMedicationName(medName);
      if (presets) setDosageUnit(presets.unit);
      if (dbMatch) {
        setName(dbMatch.productName);
        setSelectedMedicationDbId(dbMatch.medicationId);
        setSelectedDbMed(dbMatch);
        const knownRoutes = catalogRoutes(dbMatch);
        if (knownRoutes.length === 1) {
          setRoute(deserializeMedicationRoute(knownRoutes[0]));
        }
        const forms = dbMatch.dimensions.doseForms
          .map(serializeMedicationForm)
          .filter((form) => form !== null);
        if (forms.length === 1) setDeliveryMethod(forms[0]!);
      } else {
        // A name the catalogue does not answer for. It is kept as she typed
        // it and nothing is claimed about the pack behind it.
        setName(medName);
      }
      setStep(2);
    },
    [dbMedications],
  );

  const handlePackResolved = useCallback(
    (result: MedicationPackScanResult) => {
      const { resolution, catalog } = result;
      const pack = resolution.presentation;
      if (!pack || !resolution.medicationId) return;
      // The box she is holding, as the catalogue reads it. These are the
      // identity answers; she does not have to pick them again.
      setIdentity((current) => ({
        ...current,
        source: "barcode",
        scannedCode: resolution.normalizedCode,
        medicationId: resolution.medicationId,
        productName: pack.productName,
        brand: pack.brand,
        doseForm: pack.doseForm ?? "",
        strength: pack.strength ?? "",
        container: pack.container ?? "",
        packQuantity: pack.packQuantity ?? "",
      }));
      handleQuickAdd(pack.productName);
      // The pack's label is authoritative for identity; its strength is not a prescribed dose.
      setName(pack.productName);
      setDosageAmount("");
      setSelectedDbMed(null);
      const catalogUnit = catalog?.doseUnit?.trim();
      setDosageUnit(
        catalogUnit
          ? (DOSE_UNITS.find(
              (unit) => unit.toLowerCase() === catalogUnit.toLowerCase(),
            ) ?? (catalogUnit.toUpperCase() === "UI" ? "IU" : ""))
          : (getDosagePresetForMedicationName(pack.productName)?.unit ?? ""),
      );
      setSelectedMedicationDbId(resolution.medicationId);
      const catalogRoute = serializeMedicationRoute(catalog?.defaultRoute);
      const contentRoutes: PatientMedicationRoute[] = [];
      // "Other" is not a specific route. Use one known route for the matched
      // medication, or leave the field unanswered when there is no single answer.
      const knownRoute =
        catalogRoute && catalogRoute !== "other"
          ? catalogRoute
          : contentRoutes.length === 1 && contentRoutes[0] !== "other"
            ? contentRoutes[0]
            : null;
      setRoute(deserializeMedicationRoute(knownRoute));
      setDeliveryMethod(catalog?.form ?? "");
      setIsTriggerShot(pack.defaultRole === "trigger_shot");
      setPackIsTriggerCandidate(pack.therapeuticClass === "hcg");
      setScanPanelOpen(false);
    },
    [handleQuickAdd],
  );

  // Both individual and prescription saves use the same duration calculation.
  const computedEndDate = prescriptionEndDate(prescriptionDraft);

  // Submit
  // After submitting a dose period, reset dose/duration fields for a new period
  // but keep the medication identity (name, route, DB linkage, times, cycle link)
  const handleAddAnotherDosePeriod = useCallback(() => {
    // The next period starts the day after the current one ends
    const nextStartDate = computedEndDate
      ? computeEndDate(computedEndDate, 2)
      : "";

    setDosageAmount("");
    setConfirmingStartDate(false);
    setStartDate(nextStartDate);
    setEndDate("");
    setDurationPreset(null);
    setCustomDurationValue("");
    setCustomDurationUnit("days");
    setNotes("");
    // Go back to configure step (step 2) with same medication pre-filled
    setStep(2);
  }, [computedEndDate]);

  function buildInventoryEntry(
    draft = prescriptionDraft,
  ): AddMedicationInventoryEntry | null {
    const {
      inventoryQuantity,
      inventoryUnitLabel,
      route,
      deliveryMethod,
      name,
      dosageAmount,
      dosageUnit,
    } = draft;
    if (!inventoryMode) return null;
    const quantity = Number.parseInt(inventoryQuantity, 10);
    const safeQuantity =
      Number.isFinite(quantity) && quantity > 0 ? quantity : 1;
    const unitLabel =
      inventoryUnitLabel.trim() ||
      inferInventoryUnitLabel(route, deliveryMethod);

    return {
      name: name.trim(),
      dosage: formatDoseLabel(dosageAmount, dosageUnit),
      doseAmount: (() => {
        const parsed = parseDoseInput(dosageAmount);
        return parsed.ok ? parsed.value : null;
      })(),
      doseUnit: dosageUnit || null,
      route: route || null,
      routeLabel: route ? getRouteLabel(route, t) : "",
      deliveryMethod: deliveryMethod || null,
      deliveryMethodLabel: deliveryMethod
        ? getDeliveryMethodLabel(deliveryMethod, t)
        : null,
      form: inferInventoryForm(route, deliveryMethod),
      quantity: safeQuantity,
      unitLabel,
      medicationId:
        draft.selectedMedicationDbId ??
        (draft.name === prescriptionDraft.name ? resolvedMedicationDbId : null),
    };
  }

  async function saveNewMedication(
    draft: typeof prescriptionDraft,
    index?: number,
  ) {
    const {
      name,
      startDate,
      dosageAmount,
      dosageUnit,
      selectedTimes,
      notes,
      route,
      deliveryMethod,
      linkToCycle,
      includeTakingSchedule,
    } = draft;
    const parsedDose = parseDoseInput(dosageAmount);
    if (!parsedDose.ok) return false;
    const computedEndDate = prescriptionEndDate(draft);
    const shouldCreateMedication = !inventoryMode || includeTakingSchedule;
    const effectiveIsTriggerShot = isTriggerShotContext || draft.isTriggerShot;
    let resolvedMedicationDbId =
      draft.selectedMedicationDbId ??
      (draft.name === prescriptionDraft.name
        ? resolveMedicationCatalogId(null, dbMedications, draft.name)
        : null);
    // Unopened recap rows have not run the active form's catalog hook. Resolve
    // their identity through the real catalog; unavailable catalog results retain
    // the existing free-text create behavior, with a bounded request deadline.
    if (
      !resolvedMedicationDbId &&
      index !== undefined &&
      !createdPrescriptionIndices.current.has(index)
    ) {
      const controller = new AbortController();
      const deadline = setTimeout(() => controller.abort(), 20_000);
      try {
        const { data, error } = await client.GET("/medication-catalog", {
          signal: controller.signal,
          params: {
            query: {
              q: name.trim(),
              limit: 50,
              ...(patientId ? { patientId } : {}),
            },
          },
        });
        if (!error) {
          resolvedMedicationDbId = resolveMedicationCatalogId(
            null,
            data?.data.items ?? [],
            name,
          );
        }
      } catch {
        // A free-text course is still accepted by the real medication API.
      } finally {
        clearTimeout(deadline);
      }
    }
    if (index !== undefined && resolvedMedicationDbId) {
      prescriptionDrafts.current.set(index, {
        ...draft,
        selectedMedicationDbId: resolvedMedicationDbId,
      });
    }
    const inventoryEntry = inventoryMode
      ? buildInventoryEntry({
          ...draft,
          selectedMedicationDbId: resolvedMedicationDbId,
        })
      : null;
    if (
      shouldCreateMedication &&
      (index === undefined
        ? !createdSingleMedication.current
        : !createdPrescriptionIndices.current.has(index))
    ) {
      // Create mode
      const role = effectiveIsTriggerShot
        ? "trigger_shot"
        : (externalMedicationRole ?? "standard");
      const newDose = parsedDose.value;
      const newDoseUnit = dosageUnit || null;

      try {
        await createMedication.mutateAsync({
          name,
          startDate,
          endDate: computedEndDate,
          dose: newDose,
          doseUnit: newDoseUnit,
          timeTaken: selectedTimes.length > 0 ? selectedTimes : undefined,
          notes: notes || null,
          route: serializeMedicationRoute(route),
          form: deliveryMethod
            ? mapDeliveryMethodToApiForm(deliveryMethod)
            : null,
          cycleId: linkToCycle && cycleId ? cycleId : null,
          phaseId: linkToCycle && phaseId ? phaseId : null,
          medicationId: resolvedMedicationDbId,
          ...(submittedIdentity ? { identity: submittedIdentity } : {}),
          medicationRole: role,
        });
        if (index !== undefined) createdPrescriptionIndices.current.add(index);
        else createdSingleMedication.current = true;
        onMedicationCreated?.({
          name,
          dose: newDose,
          doseUnit: newDoseUnit,
        });
      } catch (err) {
        console.error("[AddMedicationModal] createMedication failed:", err);
        if (index !== undefined) {
          setPrescriptionError(t("add_medication.prescription_add_failed"));
          setPrescriptionAddFailed(true);
        } else {
          setSaveError({ kind: "save_failed", retry: "submit" });
        }
        return false;
      }

      // Give the trigger shot its durable home: the medication record has no
      // role field in the API, so reload and cross-surface behavior (stim
      // chart, cycle header, hub trigger step) derive from phase.triggerShot
      // on the cycle's protocol phase (#2224).
      if (effectiveIsTriggerShot && linkToCycle && cycleId) {
        const targetPhase = resolveTriggerShotPhase(cyclePhases, phaseId);
        if (targetPhase) {
          try {
            await updatePhase.mutateAsync({
              cycleId,
              phaseId: targetPhase.id,
              body: {
                triggerShot: buildTriggerShotPatch({
                  existing: targetPhase.triggerShot,
                  startDate,
                  time: selectedTimes[0] ?? null,
                  medicationName: name,
                }),
              },
            });
          } catch (err) {
            // The medication itself saved — surface the partial failure
            // instead of silently dropping the durable trigger date.
            console.error(
              "[AddMedicationModal] trigger phase update failed:",
              err,
            );
            triggerTimingWarning.current = true;
          }
        }
      }
    }

    if (
      inventoryEntry &&
      (index === undefined || !stockedPrescriptionIndices.current.has(index))
    ) {
      try {
        await onInventoryMedicationAdded?.(inventoryEntry);
        if (index !== undefined) stockedPrescriptionIndices.current.add(index);
      } catch (err) {
        if (index !== undefined) {
          setPrescriptionError(t("add_medication.prescription_add_failed"));
          setPrescriptionAddFailed(true);
        } else if (createdSingleMedication.current) {
          setSaveError({ kind: "stock_not_added" });
        } else {
          setSaveError({ kind: "save_failed", retry: "submit" });
        }
        console.error("[AddMedicationModal] inventory add failed:", err);
        return false;
      }
    }

    // Trigger shot → auto-open retrieval appointment
    if (
      shouldCreateMedication &&
      effectiveIsTriggerShot &&
      onRequestRetrievalAppointment &&
      selectedTimes.length > 0
    ) {
      const triggerTime = selectedTimes[0]!;
      const [hours, minutes] = triggerTime.split(":").map(Number);
      const triggerDate = new Date(startDate + "T00:00:00");
      triggerDate.setHours(hours!, minutes);
      triggerDate.setHours(triggerDate.getHours() + 36);
      const retrievalDate = `${triggerDate.getFullYear()}-${String(triggerDate.getMonth() + 1).padStart(2, "0")}-${String(triggerDate.getDate()).padStart(2, "0")}`;
      const retrievalTime = `${String(triggerDate.getHours()).padStart(2, "0")}:${String(triggerDate.getMinutes()).padStart(2, "0")}`;
      onRequestRetrievalAppointment({
        type: "Procedure",
        subType: "Egg Retrieval",
        suggestedDate: retrievalDate,
        suggestedTime: retrievalTime,
      });
    }

    return true;
  }

  async function handleAddPrescription() {
    if (prescriptionSaveInFlight.current) return;
    setPrescriptionError("");
    setPrescriptionAddFailed(false);
    const pending = ocrMedQueue
      .map((_, index) => ({ index, draft: getPrescriptionDraft(index) }))
      .filter(({ index }) => !savedPrescriptionIndices.includes(index));
    // Validate the whole prescription before making the first write. Missing
    // extraction needs an edit, not an invented dose or route.
    for (const { index, draft } of pending) {
      const shouldSchedule = !inventoryMode || draft.includeTakingSchedule;
      if (shouldSchedule && !draft.startDate) {
        openPrescriptionMedication(index, true);
        setPrescriptionError(
          t("add_medication.confirm_start_date_to_schedule"),
        );
        return;
      }
      const dose = parseDoseInput(draft.dosageAmount);
      const end = prescriptionEndDate(draft);
      const outsideCycle =
        shouldSchedule &&
        cycleDateBounds &&
        (draft.startDate < cycleDateBounds.startDate ||
          (!!cycleDateBounds.endDate &&
            (draft.startDate > cycleDateBounds.endDate ||
              (!!end && end > cycleDateBounds.endDate))));
      if (
        !draft.name.trim() ||
        !dose.ok ||
        dose.value === null ||
        !draft.dosageUnit ||
        !draft.route ||
        (shouldSchedule &&
          !addMedicationSchema.safeParse({
            name: draft.name,
            dosageAmount: draft.dosageAmount,
            doseUnit: draft.dosageUnit,
            startDate: draft.startDate,
            endDate: end,
          }).success) ||
        draft.customTimeValue.trim() ||
        outsideCycle
      ) {
        openPrescriptionMedication(index);
        if (!dose.ok) setDoseError(dose.reason);
        setPrescriptionError(t("add_medication.prescription_complete_details"));
        return;
      }
    }
    prescriptionSaveInFlight.current = true;
    setIsSavingPrescription(true);
    let completed = false;
    try {
      for (const { index, draft } of pending) {
        prescriptionDrafts.current.set(index, draft);
        if (!(await saveNewMedication(draft, index))) return;
        setSavedPrescriptionIndices((indices) =>
          indices.includes(index) ? indices : [...indices, index],
        );
      }
      completed = true;
    } finally {
      prescriptionSaveInFlight.current = false;
      setIsSavingPrescription(false);
    }
    if (completed) {
      const addedCount = ocrMedQueue.length;
      const hasTriggerTimingWarning = triggerTimingWarning.current;
      handleClose();
      if (!hasTriggerTimingWarning) showAddedConfirmation(addedCount);
    }
  }

  function showAddedConfirmation(count: number) {
    if (count < 1) return;
    const message = t(
      `add_medication.medications_added_${count === 1 ? "one" : "other"}`,
      { params: { count: String(count) } },
    );
    // The app-level toast outlives this dialog. Let its closing transition
    // finish before starting the visible confirmation and success haptic.
    setTimeout(() => {
      haptic("success");
      showToast({
        message,
        variant: "success",
        duration: 4000,
        motion: "rise",
      });
    }, 350);
  }

  function findDuplicateMedication() {
    // Compare on the brand short name — the catalog ships entries whose full
    // label is the regulatory string ("CETROTIDE 0,25 mg, poudre..."), while
    // the form carries just "Cetrotide". A raw strcmp would miss the match.
    const normalizedName = extractMedicationShortName(name).toLowerCase();
    if (!normalizedName || selectedTimes.length === 0) return null;
    const parsedDose = parseDoseInput(dosageAmount);
    const normalizedDose = parsedDose.ok ? parsedDose.value : null;
    const normalizedUnit = dosageUnit.trim().toLowerCase() || null;
    const todayDate = new Date().toISOString().slice(0, 10);
    for (const med of patientMedications) {
      if (isMedicationStopped(med.notes)) continue;
      if (med.endDate && med.endDate < todayDate) continue;
      // Clinic-owned prescriptions are read-only everywhere else; offering
      // Replace here would just hit a server rejection with a generic error.
      if (isProviderOwnedMedication(med.source)) continue;
      const rawMedName =
        med.displayName ?? med.medication?.name ?? med.rawMedicationName ?? "";
      const medName = extractMedicationShortName(rawMedName).toLowerCase();
      if (!medName || medName !== normalizedName) continue;
      // Different strengths of the same brand (Gonal-F 450 IU vs 900 IU) are
      // different products — don't collapse them into a duplicate warning.
      const medDose = med.dose ?? null;
      const medUnit = med.doseUnit?.trim().toLowerCase() || null;
      if (medDose !== normalizedDose || medUnit !== normalizedUnit) continue;
      const overlap = (med.timeTaken ?? []).find((t) =>
        selectedTimes.includes(t),
      );
      if (!overlap) continue;
      return {
        medicationId: med.id,
        name: extractMedicationShortName(rawMedName) || rawMedName || name,
        time: overlap,
        notes: med.notes ?? null,
      };
    }
    return null;
  }

  async function handleSubmit(options?: {
    bypassDuplicateCheck?: boolean;
    replacing?: {
      medicationId: string;
      notes: string | null;
    };
  }) {
    setSaveError(null);
    // An undated course stays undated on edit unless she picks a date.
    const keepsUnknownStartDate = !!editMedication && !editMedication.startDate;
    if (
      (!inventoryMode || includeTakingSchedule) &&
      !startDate &&
      !keepsUnknownStartDate
    ) {
      setConfirmingStartDate(true);
      setStep(2);
      return;
    }
    // Field-level dose validation shared with add-another and OCR (#2228):
    // bare parseFloat accepted "12abc" as 12 and "1,5" as 1, and let zero or
    // negative doses through to the API.
    const parsedDose = parseDoseInput(dosageAmount);
    if (!parsedDose.ok) {
      setDoseError(parsedDose.reason);
      return;
    }

    if (
      !editMedication &&
      (!inventoryMode || includeTakingSchedule) &&
      !options?.bypassDuplicateCheck
    ) {
      const match = findDuplicateMedication();
      if (match) {
        setDuplicateMatch(match);
        return;
      }
    }

    if ((!inventoryMode || includeTakingSchedule) && cycleDateBounds) {
      const startsBeforeCycle =
        !!startDate && startDate < cycleDateBounds.startDate;
      const startsAfterCycle =
        !!startDate &&
        !!cycleDateBounds.endDate &&
        startDate > cycleDateBounds.endDate;
      const endsAfterCycle =
        !!cycleDateBounds.endDate &&
        !!computedEndDate &&
        computedEndDate > cycleDateBounds.endDate;
      if (startsBeforeCycle || startsAfterCycle || endsAfterCycle) {
        setSaveError({ kind: "date_outside_cycle" });
        return;
      }
    }

    if (editMedication) {
      // Edit mode — update existing medication
      const nextDose = parsedDose.value;
      const nextDoseUnit = dosageUnit || null;
      try {
        // A name edited here is a patient display override (#319): it
        // persists as displayName without ever touching catalog names.
        // Restoring the original name clears the override.
        const trimmedName = name.trim();
        const displayNameUpdate =
          trimmedName && trimmedName !== editMedication.name
            ? { displayName: trimmedName }
            : {};
        await updateMedication.mutateAsync({
          medicationId: editMedication.id,
          ...displayNameUpdate,
          ...(startDate ? { startDate } : {}),
          endDate: computedEndDate || null,
          dose: nextDose,
          doseUnit: nextDoseUnit,
          // The edit form always shows the record's current schedule, so an
          // empty selection is the user explicitly clearing every time — send
          // [] (which also disables the linked reminder) rather than omitting
          // the field, which the update hook reads as "leave times unchanged"
          // (#2227/#2233). Create mode keeps omitting when empty.
          timeTaken: selectedTimes,
          notes: notes || null,
          route: serializeMedicationRoute(route),
          form: deliveryMethod
            ? mapDeliveryMethodToApiForm(deliveryMethod)
            : null,
          // The "is this a trigger shot?" toggle is shown in edit mode and was
          // read by neither save branch, so a medication filed under the wrong
          // step could only be corrected by deleting and re-adding it. It
          // decides which tab the medication appears on, so it has to be
          // editable there.
          medicationRole: effectiveIsTriggerShot ? "trigger_shot" : "standard",
        });
        onMedicationUpdated?.({
          id: editMedication.id,
          name: trimmedName || editMedication.name,
          dose: nextDose,
          doseUnit: nextDoseUnit,
          previous: {
            dose: editMedication.dose ?? null,
            doseUnit: editMedication.doseUnit ?? null,
          },
        });
        handleClose();
      } catch (err) {
        console.error("[AddMedicationModal] updateMedication failed:", err);
        setSaveError({ kind: "save_failed", retry: "submit" });
      }
      return;
    }

    if (!(await saveNewMedication(prescriptionDraft))) return;

    // Replace flow: the new course has saved. Now stop the previous one on the
    // day before this new one starts (in the patient's local day), so the two
    // courses don't overlap and the schedule has no gap.
    if (options?.replacing && startDate) {
      try {
        await updateMedication.mutateAsync({
          medicationId: options.replacing.medicationId,
          endDate: addDaysToLocalDate(startDate, -1),
          notes: prependStoppedMedicationNote(options.replacing.notes, null),
        });
      } catch (err) {
        console.error(
          "[AddMedicationModal] stop replaced medication failed:",
          err,
        );
        setSaveError({ kind: "save_failed", retry: "submit" });
        return;
      }
    }

    const hasTriggerTimingWarning = triggerTimingWarning.current;
    handleClose();
    if (!hasTriggerTimingWarning) showAddedConfirmation(1);
  }

  // Submit current entry and start a new dose period for the same medication
  const handleSubmitAndAddAnother = useCallback(async () => {
    setSaveError(null);
    if (!startDate) {
      setConfirmingStartDate(true);
      setStep(2);
      return;
    }
    // Same field-level validation as the normal submit path (#2228).
    const parsedDose = parseDoseInput(dosageAmount);
    if (!parsedDose.ok) {
      setDoseError(parsedDose.reason);
      return;
    }

    if (cycleDateBounds) {
      const startsBeforeCycle = startDate < cycleDateBounds.startDate;
      const startsAfterCycle =
        !!cycleDateBounds.endDate && startDate > cycleDateBounds.endDate;
      const endsAfterCycle =
        !!cycleDateBounds.endDate &&
        !!computedEndDate &&
        computedEndDate > cycleDateBounds.endDate;
      if (startsBeforeCycle || startsAfterCycle || endsAfterCycle) {
        setSaveError({ kind: "date_outside_cycle" });
        return;
      }
    }

    const role = effectiveIsTriggerShot
      ? "trigger_shot"
      : (externalMedicationRole ?? "standard");

    try {
      await createMedication.mutateAsync({
        name,
        startDate,
        endDate: computedEndDate,
        dose: parsedDose.value,
        doseUnit: dosageUnit || null,
        timeTaken: selectedTimes.length > 0 ? selectedTimes : undefined,
        notes: notes || null,
        route: serializeMedicationRoute(route),
        form: deliveryMethod
          ? mapDeliveryMethodToApiForm(deliveryMethod)
          : null,
        cycleId: linkToCycle && cycleId ? cycleId : null,
        phaseId: linkToCycle && phaseId ? phaseId : null,
        medicationId: resolvedMedicationDbId,
        // What the pack is, as she answered for it. The server resolves the
        // pack from these values rather than taking one the client picked.
        ...(submittedIdentity ? { identity: submittedIdentity } : {}),
        medicationRole: role,
      });
    } catch (err) {
      // Keep the current dose period on screen for retry — advancing (or an
      // unhandled rejection) would silently drop the entry (#2228).
      console.error(
        "[AddMedicationModal] createMedication (add another) failed:",
        err,
      );
      setSaveError({ kind: "save_failed", retry: "submit_and_add_another" });
      return;
    }

    handleAddAnotherDosePeriod();
  }, [
    cycleDateBounds,
    startDate,
    computedEndDate,
    createMedication,
    submittedIdentity,
    name,
    dosageAmount,
    dosageUnit,
    selectedTimes,
    notes,
    route,
    linkToCycle,
    cycleId,
    phaseId,
    resolvedMedicationDbId,
    effectiveIsTriggerShot,
    externalMedicationRole,
    handleAddAnotherDosePeriod,
    deliveryMethod,
  ]);

  // Shared styles
  // ============================================================================
  // Step 0 — Entry Method
  // ============================================================================

  function renderStep0() {
    return (
      <ScrollView
        contentContainerStyle={{ padding: 24 }}
        keyboardShouldPersistTaps="handled"
        showsVerticalScrollIndicator={false}
      >
        {scanPanelOpen ? (
          <MedicationPackScanPanel
            countryCode={patientCountryCode}
            onResolved={handlePackResolved}
            onSearchInstead={() => {
              setScanPanelOpen(false);
            }}
          />
        ) : (
          <>
            {renderMedicationSearch()}
            {!name.trim() && !isSearchActive ? (
              <>
                {cycleId && (isQuickAddPending || quickAddItems.length > 0) ? (
                  <MoonFormField
                    label={t("add_medication.quick_add")}
                    labelIcon={Zap}
                    style={{ marginBottom: 20 }}
                  >
                    <ScrollView
                      horizontal
                      testID="medication-quick-add-scroll"
                      showsHorizontalScrollIndicator={false}
                      keyboardShouldPersistTaps="handled"
                      nestedScrollEnabled
                      style={{ flexGrow: 0 }}
                      contentContainerStyle={{ gap: 8, alignItems: "center" }}
                    >
                      {isQuickAddPending
                        ? [0, 1, 2].map((index) => (
                            <MoonSkeleton
                              key={`quick-add-skeleton-${index}`}
                              width={132}
                              height={36}
                              radius={18}
                              // The shared skeleton animates its own
                              // opacity, which on this dialog's light
                              // surface is not visible at all — the row
                              // read as an empty gap under its heading.
                              style={{ backgroundColor: "rgba(0, 0, 0, 0.10)" }}
                            />
                          ))
                        : null}
                      {quickAddItems.map((item) => (
                        <CompactActionButton
                          key={item.label}
                          label={item.label}
                          action="quick_add_medication"
                          variant="secondary"
                          size="sm"
                          style={{ flexShrink: 0 }}
                          leading={medicationIllustration(item.label, 24)}
                          onPress={() => {
                            if (item.kind === "product") {
                              handleSelectResult({
                                id: item.product.medicationId,
                                name: item.product.productName,
                                subtitle: item.product.ingredients.join(" · "),
                                dbMed: item.product,
                              });
                              return;
                            }
                            // A search chip has no pack behind it; it says
                            // what to look for and the catalogue answers.
                            setSearchTerm(item.query);
                          }}
                        />
                      ))}
                    </ScrollView>
                  </MoonFormField>
                ) : null}
                <View style={{ gap: 10 }}>
                  <Row
                    surface="input"
                    title={t("add_medication.entry_scan_prescription")}
                    leading={
                      <Image
                        source={scanPrescriptionCamera}
                        style={{ width: 34, height: 34 }}
                        resizeMode="contain"
                        accessible={false}
                      />
                    }
                    disabled={isExtracting}
                    accessibilityState={{ busy: isExtracting }}
                    trailing={
                      isExtracting ? (
                        <ActivityIndicator
                          size="small"
                          color={EMBER.colors.textMid}
                        />
                      ) : undefined
                    }
                    onPress={() => void handleScanPrescription()}
                    action="add_medication.entry_scan_prescription"
                  />
                  {scanError === "photo_access_needed" ? (
                    <ActionBanner
                      variant="important"
                      title={t("add_medication.alert_permission_title")}
                      message={t("add_medication.alert_permission_message")}
                    />
                  ) : null}
                  <Row
                    surface="input"
                    title={t("add_medication.pack_scan.scan_pack_code")}
                    leading={
                      <Image
                        source={scanPackBarcode}
                        style={{ width: 34, height: 34 }}
                        resizeMode="contain"
                        accessible={false}
                      />
                    }
                    disabled={isExtracting}
                    accessibilityState={{ expanded: scanPanelOpen }}
                    onPress={() => {
                      setScanPanelOpen(true);
                    }}
                    action="add_medication.scan_pack_code"
                  />
                </View>
              </>
            ) : null}
          </>
        )}
      </ScrollView>
    );
  }

  function renderMedicationSearch() {
    return (
      <MoonFormField
        label={t("add_medication.medication_name")}
        labelIcon={Pill}
        style={{ marginBottom: name.trim() ? 0 : 20 }}
      >
        <MoonSearchInput
          value={searchTerm}
          onChangeText={setSearchTerm}
          placeholder={
            isTriggerShotContext
              ? t("add_medication.trigger_search_placeholder")
              : t("add_medication.search_placeholder")
          }
          accessibilityLabel={t("add_medication.medication_name")}
          onClear={() => setSearchTerm("")}
          loading={isCatalogLoading}
        />
        {/* One box, one height, whatever is in it. Searching has four states
            — waiting on the debounce, loading, results, nothing found — and
            letting each size itself moved the dialog on every keystroke. The
            box is the same size for all four; its contents scroll. Below two
            characters there is no box at all. */}
        {isSearchActive ? (
          <ScrollView
            testID="medication-search-results"
            style={{ height: SEARCH_RESULTS_MAX_HEIGHT, marginTop: 12 }}
            contentContainerStyle={{ gap: 8 }}
            nestedScrollEnabled
            keyboardShouldPersistTaps="handled"
            showsVerticalScrollIndicator={false}
          >
            {searchResults.map((result) => (
              <Row
                key={result.id}
                surface="input"
                title={result.name}
                subtitle={result.subtitle || undefined}
                leading={medicationIllustration(result.name)}
                titleRight={
                  <>
                    {getSearchResultRoutes(result).map((route) => (
                      <View key={route} testID="medication-result-route">
                        <Tag
                          label={t(ROUTE_PRESENTATION[route].labelKey)}
                          tone="neutral"
                        />
                      </View>
                    ))}
                    {/* A different tone from the routes beside it: one says
                        how the medication is given, the other which market
                        it is sold in, and in the same grey they read as one
                        list of facts about the same thing. */}
                    {result.dbMed.market ? (
                      <View testID="medication-result-market">
                        <Tag label={result.dbMed.market} tone="sourceBlue" />
                      </View>
                    ) : null}
                  </>
                }
                accessibilityLabel={result.name}
                action="select_medication_result"
                onPress={() => handleSelectResult(result)}
              />
            ))}
            {searchResults.length === 0 && isCatalogLoading
              ? [0, 1, 2].map((index) => (
                  <MoonSkeleton
                    key={`search-skeleton-${index}`}
                    width="100%"
                    height={56}
                    radius={14}
                    style={{ backgroundColor: "rgba(0, 0, 0, 0.06)" }}
                  />
                ))
              : null}
            {searchResults.length === 0 && !isCatalogLoading ? (
              <Row
                surface="input"
                title={t("add_medication.use_typed_medication", {
                  params: { medication: searchTerm.trim() },
                })}
                action="use_typed_medication"
                onPress={() => handleQuickAdd(searchTerm.trim())}
              />
            ) : null}
          </ScrollView>
        ) : null}
      </MoonFormField>
    );
  }

  // ============================================================================
  // Step 1 — Read prescription together before opening any individual form
  // ============================================================================

  function renderPrescriptionRecap() {
    return (
      <View style={{ flexShrink: 1 }}>
        <ScrollView
          testID="medication-prescription-recap"
          contentContainerStyle={{ padding: 24, gap: 12 }}
          showsVerticalScrollIndicator={false}
        >
          {ocrMedQueue.map((med, index) => {
            const draft = getPrescriptionDraft(index);
            const saved = savedPrescriptionIndices.includes(index);
            const medicationName = draft.name;
            const unit = draft.dosageUnit;
            const dose = formatDoseLabel(
              draft.dosageAmount,
              DOSE_UNIT_KEYS[unit] ? t(DOSE_UNIT_KEYS[unit]) : unit,
            );
            const medicationRoute = draft.route;
            const printedTimeKey = PRESCRIPTION_TIME_KEYS[med.timeOfDay];
            const sourceMedicationChanged =
              draft.name !== prescriptionFormFields(med).name;
            const printedTiming = sourceMedicationChanged
              ? t("add_medication.prescription_timing_to_confirm")
              : [
                  med.frequency,
                  printedTimeKey ? t(printedTimeKey) : med.timeOfDay,
                ]
                  .filter(Boolean)
                  .join(" · ") ||
                med.instructions ||
                t("add_medication.prescription_timing_to_confirm");
            const changedTimes =
              JSON.stringify(draft.selectedTimes) !==
              JSON.stringify(mapTimeFromOCR(med.timeOfDay));
            const timing = changedTimes
              ? draft.selectedTimes.length > 0
                ? formatTimeSummary(draft.selectedTimes, t)
                : t("add_medication.prescription_timing_to_confirm")
              : printedTiming;
            const artwork = medicationHeaderArtwork(
              medicationName,
              draft.deliveryMethod,
            );
            return (
              <CardSurface
                key={index}
                testID={`prescription-medication-${index}`}
                // Use the dialog's blur: a nested card filter picks up the
                // copper footer button and creates an orange fade while scrolling.
                style={[inheritedDialogBlurStyle, { padding: 16 }]}
              >
                <Row
                  surface="plain"
                  title={medicationName}
                  leading={
                    <Image
                      source={artwork.source}
                      style={{ width: 40, height: 40 }}
                      resizeMode="contain"
                    />
                  }
                  leadingSize={40}
                  subtitle={[
                    dose || t("add_medication.prescription_dose_to_confirm"),
                    medicationRoute
                      ? getRouteLabel(medicationRoute, t)
                      : t("add_medication.prescription_route_to_confirm"),
                  ]
                    .filter(Boolean)
                    .join(" · ")}
                  paddingHorizontal={0}
                  paddingVertical={0}
                  trailing={
                    saved ? (
                      <Tag
                        label={t(
                          "add_medication.prescription_medication_added",
                        )}
                      />
                    ) : (
                      <IconButton
                        accessibilityLabel={t(
                          "add_medication.prescription_edit_medication",
                        )}
                        action="edit_prescription_medication"
                        disabled={
                          isSavingPrescription ||
                          createdPrescriptionIndices.current.has(index) ||
                          stockedPrescriptionIndices.current.has(index)
                        }
                        size={32}
                        surface="frosted"
                        onPress={() => openPrescriptionMedication(index)}
                      >
                        <Pencil
                          size={14}
                          color={semanticColors.text.secondary}
                          strokeWidth={1.8}
                        />
                      </IconButton>
                    )
                  }
                  chevron={false}
                />
                <MoonDetailRow
                  icon={Clock}
                  label={t("add_medication.summary_schedule")}
                  value={
                    timing === med.instructions ? (
                      <CardSubtitleText>{timing}</CardSubtitleText>
                    ) : (
                      timing
                    )
                  }
                  style={{ marginTop: 12 }}
                />
                {draft.notes && draft.notes !== med.instructions ? (
                  <MoonDetailRow
                    icon={NotebookText}
                    label={t("add_medication.summary_notes")}
                    value={<CardSubtitleText>{draft.notes}</CardSubtitleText>}
                    style={{ marginTop: 12 }}
                  />
                ) : null}
                {inventoryMode ? (
                  <MoonDetailRow
                    icon={Package}
                    label={t("add_medication.inventory.stock_on_hand_label")}
                    value={`${buildInventoryEntry(draft)?.quantity ?? 1} ${buildInventoryEntry(draft)?.unitLabel ?? ""}`}
                    style={{ marginTop: 12 }}
                  />
                ) : null}
                <View
                  style={{
                    flexDirection: "row",
                    flexWrap: "wrap",
                    gap: 12,
                    marginTop: 12,
                  }}
                >
                  <MoonDetailRow
                    icon={CalendarDays}
                    label={t("add_medication.summary_start_date")}
                    value={
                      draft.startDate
                        ? formatDate(draft.startDate)
                        : t("add_medication.start_date_to_confirm")
                    }
                    style={{ flexGrow: 1, flexBasis: "44%" }}
                  />
                  {prescriptionEndDate(draft) ? (
                    <MoonDetailRow
                      icon={CalendarDays}
                      label={t("add_medication.summary_end_date")}
                      value={formatDate(prescriptionEndDate(draft)!)}
                      style={{ flexGrow: 1, flexBasis: "44%" }}
                    />
                  ) : null}
                  <MoonDetailRow
                    icon={Bell}
                    label={t("add_medication.prescription_reminders")}
                    value={
                      draft.selectedTimes.length > 0
                        ? draft.selectedTimes.join(" · ")
                        : t("add_medication.prescription_no_reminders")
                    }
                    style={{ flexGrow: 1, flexBasis: "44%" }}
                  />
                </View>
              </CardSurface>
            );
          })}
        </ScrollView>
        <View
          style={{
            paddingHorizontal: 24,
            paddingTop: 12,
            paddingBottom: 24,
            gap: 12,
          }}
        >
          {prescriptionError ? (
            <ActionBanner
              variant="important"
              message={prescriptionError}
              actionLabel={
                prescriptionAddFailed ? t("clinical_data.retry") : undefined
              }
              action="retry_add_prescription_medications"
              actionIcon={RotateCcw}
              actionLoading={isSavingPrescription}
              onAction={
                prescriptionAddFailed
                  ? () => void handleAddPrescription()
                  : undefined
              }
            />
          ) : null}
          <PrimaryButton
            label={t("add_medication.prescription_add_medications")}
            action="add_prescription_medications"
            icon={
              <Plus
                size={18}
                color={semanticColors.action.primary.foreground}
              />
            }
            onPress={() => void handleAddPrescription()}
            loading={isSavingPrescription}
          />
        </View>
      </View>
    );
  }

  // ============================================================================
  // Step 2 — Configure
  // ============================================================================

  function renderStep2() {
    const showScheduleFields = !inventoryMode || includeTakingSchedule;
    const stockUnit =
      inventoryUnitLabel.trim() ||
      inferInventoryUnitLabel(route, deliveryMethod);
    // A unit set earlier that this route does not count in stays offered.
    const routeStockUnits = inventoryUnitsForRoute(route);
    const stockUnits = routeStockUnits.includes(stockUnit)
      ? routeStockUnits
      : [...routeStockUnits, stockUnit];
    const stockUnitLabel = (unit: string) =>
      INVENTORY_UNITS.includes(unit)
        ? t(`add_medication.inventory.stock_unit.${unit}`)
        : unit;

    return (
      <ScrollView
        ref={configureScrollRef}
        contentContainerStyle={{ padding: 24 }}
        showsVerticalScrollIndicator={false}
      >
        {scanError === "no_medications_found" ? (
          <ActionBanner
            variant="important"
            title={t("add_medication.alert_ocr_no_meds_title")}
            message={t("add_medication.alert_ocr_no_meds_message")}
            style={{ marginBottom: 16 }}
          />
        ) : scanError === "prescription_unreadable" ? (
          <ActionBanner
            variant="important"
            title={t("add_medication.alert_ocr_failed_title")}
            message={t("add_medication.alert_ocr_failed_message")}
            actionLabel={unreadableScan ? t("clinical_data.retry") : undefined}
            action="retry_read_prescription"
            actionIcon={RotateCcw}
            actionLoading={isExtracting}
            onAction={
              unreadableScan
                ? () =>
                    void readPrescription(
                      unreadableScan.file,
                      unreadableScan.uploadId,
                    )
                : undefined
            }
            style={{ marginBottom: 16 }}
          />
        ) : null}
        {ocrMedQueue.length > 0 && prescriptionError ? (
          <ActionBanner
            variant="important"
            message={prescriptionError}
            actionLabel={
              prescriptionAddFailed ? t("clinical_data.retry") : undefined
            }
            action="retry_add_prescription_medications"
            actionIcon={RotateCcw}
            actionLoading={isSavingPrescription}
            onAction={
              prescriptionAddFailed
                ? () => void handleAddPrescription()
                : undefined
            }
            style={{ marginBottom: 16 }}
          />
        ) : null}
        {replacingMedicationName ? (
          <ActionBanner
            variant="info"
            message={t("care.protocol_notes.swap_replacing_banner", {
              params: { medication: replacingMedicationName },
            })}
            style={{ marginBottom: 16 }}
          />
        ) : null}

        {ocrMedQueue.length > 1 ? (
          <ActionBanner
            variant="info"
            title={t("add_medication.ocr_queue_progress", {
              params: {
                current: String(ocrMedIndex + 1),
                total: String(ocrMedQueue.length),
              },
            })}
            style={{ marginBottom: 16 }}
          />
        ) : null}

        {isTriggerShotContext ? (
          <ActionBanner
            variant="info"
            title={t("add_medication.trigger_context_title")}
            message={t("add_medication.trigger_context_body")}
            style={{ marginBottom: 16 }}
          />
        ) : null}

        {isEditMode || !medicationSelected ? (
          <MoonFormField
            label={t("add_medication.medication_name")}
            labelIcon={Pill}
            style={{ marginBottom: 16 }}
          >
            {/* The canonical field, the route menu inside it after the name,
              as a unit menu sits after a dose. */}
            <View style={{ position: "relative", zIndex: 20 }}>
              <MoonValueInput
                value={name}
                onChangeText={setName}
                placeholder={t("add_medication.medication_name")}
                accessibilityLabel={t("add_medication.medication_name")}
                focused={showRouteDropdown}
                trailing={
                  route ? (
                    <Pressable
                      ref={routeTriggerRef}
                      onPress={() => {
                        capturePress(
                          pressLabel(t("add_medication.summary_route")),
                          "open_route_dropdown",
                        );
                        setShowDoseUnitDropdown(false);
                        setShowRouteDropdown((current) => !current);
                      }}
                      accessibilityRole="button"
                      accessibilityLabel={t("add_medication.summary_route")}
                      accessibilityState={{ expanded: showRouteDropdown }}
                      className="flex-row items-center"
                      style={fieldMenuTriggerStyle}
                    >
                      {(() => {
                        const opt = ROUTE_OPTIONS.find(
                          (o) => o.value === route,
                        );
                        const IconComp = opt?.icon ?? Pill;
                        return <IconComp size={13} color={EMBER.colors.warm} />;
                      })()}
                      <Text
                        className="font-dm-sans"
                        style={{
                          fontSize: 13,
                          fontWeight: "500",
                          color: EMBER.colors.warm,
                        }}
                      >
                        {(() => {
                          const opt = ROUTE_OPTIONS.find(
                            (o) => o.value === route,
                          );
                          return opt ? t(opt.labelKey) : route;
                        })()}
                      </Text>
                      <ChevronDown size={12} color={EMBER.colors.textLight} />
                    </Pressable>
                  ) : undefined
                }
              />
              {route ? (
                <AnchoredSelectMenu
                  visible={showRouteDropdown}
                  triggerRef={routeTriggerRef}
                  value={route}
                  items={routeMenuItems}
                  align="right"
                  width={200}
                  maxHeight={340}
                  accentColor={EMBER.colors.warm}
                  captureEscape={false}
                  testID="medication-route-menu"
                  onClose={dismissSelectionMenus}
                  onSelect={(nextRoute) => {
                    handleRouteChange(nextRoute);
                    setShowRouteDropdown(false);
                  }}
                />
              ) : null}
            </View>
          </MoonFormField>
        ) : (
          <MedicationIdentitySection
            value={identity}
            onChange={setIdentity}
            product={selectedDbMed}
            productSelected={!!name.trim()}
            productLeading={medicationIllustration(name)}
            renderPackLeading={(pack) => (
              <MedFormIcon form={packFormFor(pack)} size={24} />
            )}
            onRoutesChange={setPackRoutes}
            onChangeProduct={() => {
              // Back to an empty search, not to the name she is replacing:
              // "Change" means she wants a different medication, and leaving
              // the old one in the box makes her clear it first.
              setConfirmingStartDate(false);
              setMedicationSelected(false);
              setSelectedDbMed(null);
              setSelectedMedicationDbId(null);
              setName("");
              setSearchTerm("");
              setStep(0);
            }}
          />
        )}

        {/* Trigger shot toggle — contextual, only for hCG meds */}
        {showTriggerToggle && !isTriggerShotContext ? (
          // The field chrome in every state: the toggle shows on or off, so
          // the row does not turn amber.
          <View
            className="flex-row items-center justify-between"
            style={{ ...toggleRowStyle, paddingVertical: 10 }}
          >
            <Text
              className="font-dm-sans"
              style={{ fontSize: 14, color: EMBER.colors.text }}
            >
              {t("add_medication.is_trigger_shot")}
            </Text>
            <MoonToggle value={isTriggerShot} onChange={setIsTriggerShot} />
          </View>
        ) : null}

        {/* Inline dosage: [ amount |▼ unit ], under the same field label
            as every other section. */}
        <MoonFormField
          label={t("add_medication.dosage")}
          error={
            doseError ? t(`add_medication.dose_error_${doseError}`) : undefined
          }
        >
          {/* The canonical field states: the subtle border while idle,
              typing or choosing a unit, coral on an error. */}
          <View style={{ position: "relative", zIndex: 10 }}>
            <MoonValueInput
              value={dosageAmount}
              onChangeText={(text) => {
                setDosageAmount(text);
                if (doseError) setDoseError(null);
              }}
              placeholder="e.g., 150"
              keyboardType="decimal-pad"
              accessibilityLabel={t("add_medication.dosage")}
              hasError={!!doseError}
              focused={showDoseUnitDropdown}
              trailing={
                <Pressable
                  ref={doseUnitTriggerRef}
                  onPress={() => {
                    capturePress(
                      pressLabel(t("add_medication.summary_dosage")),
                      "open_dose_unit_dropdown",
                    );
                    setShowRouteDropdown(false);
                    setShowDoseUnitDropdown((current) => !current);
                  }}
                  accessibilityRole="button"
                  accessibilityLabel={t("add_medication.summary_dosage")}
                  accessibilityState={{ expanded: showDoseUnitDropdown }}
                  className="flex-row items-center justify-center"
                  style={fieldMenuTriggerStyle}
                >
                  <Text
                    className="font-dm-sans"
                    style={{
                      fontSize: 14,
                      fontWeight: "500",
                      color: dosageUnit
                        ? EMBER.colors.text
                        : EMBER.colors.textFaint,
                    }}
                  >
                    {dosageUnit || "unit"}
                  </Text>
                  <ChevronDown size={14} color={EMBER.colors.textMid} />
                </Pressable>
              }
            />

            <AnchoredSelectMenu
              visible={showDoseUnitDropdown}
              triggerRef={doseUnitTriggerRef}
              value={dosageUnit}
              items={doseUnitMenuItems}
              align="right"
              width={150}
              maxHeight={300}
              accentColor={EMBER.colors.warm}
              captureEscape={false}
              testID="medication-dose-unit-menu"
              onClose={dismissSelectionMenus}
              onSelect={(unit) => {
                setDosageUnit(unit);
                setShowDoseUnitDropdown(false);
              }}
            />
          </View>
        </MoonFormField>

        {/* Quick-select dosage presets for known medications */}
        {(() => {
          const presets = getDosagePresetForMedicationName(name);
          if (!presets) return null;
          return (
            // Common doses as chips, each as wide as its dose.
            <View
              style={{
                marginTop: 8,
                flexDirection: "row",
                flexWrap: "wrap",
                gap: 8,
              }}
            >
              {presets.doses.map((dose) => (
                <MoonFilterChip
                  key={dose}
                  label={`${dose} ${presets.unit}`}
                  selected={
                    dosageUnit === presets.unit && dosageAmount === String(dose)
                  }
                  onPress={() => {
                    setDosageAmount(String(dose));
                    setDosageUnit(presets.unit);
                  }}
                  action="dose_preset"
                />
              ))}
            </View>
          );
        })()}
        <View style={{ marginBottom: 16 }} />

        {/* Route. One reachable route is not a question: the pack answered it
            above, and the summary states it. Asking again would be asking her
            to confirm the only option there is. */}
        {(isEditMode && !route) || routeChoices.length > 1 ? (
          <>
            <MoonFormField label={t("add_medication.route")}>
              <InlineOptionPicker
                options={routeChoices.map((opt) => ({
                  id: opt.value,
                  label: t(opt.labelKey),
                  icon: opt.icon,
                  action: "medication_route",
                }))}
                value={route}
                onChange={handleRouteChange}
                wrap
              />
            </MoonFormField>
            <View style={{ marginBottom: 16 }} />
          </>
        ) : null}

        {/* Delivery method — only for injectable routes */}
        {INJECTABLE_ROUTES.includes(route) ? (
          <>
            <MoonFormField label={t("add_medication.delivery_method")}>
              <InlineOptionPicker
                options={INJECTION_DELIVERY_METHODS.map((opt) => ({
                  id: opt.value,
                  label: t(opt.labelKey),
                  icon: Syringe,
                  action: "delivery_method",
                }))}
                value={deliveryMethod}
                // Tapping the selected method again clears it.
                onChange={(method) =>
                  setDeliveryMethod(deliveryMethod === method ? "" : method)
                }
                wrap
              />
            </MoonFormField>
            <View style={{ marginBottom: 16 }} />
          </>
        ) : null}

        {inventoryMode ? (
          <>
            <MoonFormField
              label={t("add_medication.inventory.stock_on_hand_label")}
              hint={t("add_medication.inventory.stock_on_hand_helper")}
              style={{ marginBottom: 10 }}
            >
              {/* The count with its unit inside the field: the units this
                  medication's stock is counted in, as a choice when there
                  are several. */}
              <MoonValueInput
                value={inventoryQuantity}
                onChangeText={(text) =>
                  setInventoryQuantity(text.replace(/\D/g, ""))
                }
                placeholder="1"
                keyboardType="number-pad"
                accessibilityLabel={t(
                  "add_medication.inventory.stock_on_hand_label",
                )}
                suffix={
                  stockUnits.length === 1
                    ? stockUnitLabel(stockUnit)
                    : undefined
                }
                trailing={
                  stockUnits.length > 1 ? (
                    <MoonUnitToggle
                      units={stockUnits}
                      value={stockUnit}
                      onChange={(unit) => {
                        inventoryUnitPicked.current = true;
                        setInventoryUnitLabel(unit);
                      }}
                      getLabel={stockUnitLabel}
                      accessibilityLabel={t("test_result.unit")}
                      action="inventory_stock_unit"
                    />
                  ) : undefined
                }
              />
            </MoonFormField>

            {/* Stock alone records what she has; this switch also turns it
                into a dose schedule, whose fields open below. */}
            <Pressable
              onPress={() => {
                capturePress(
                  pressLabel(
                    t("add_medication.inventory.also_create_a_dose_schedule"),
                  ),
                  "toggle_taking_schedule",
                );
                setIncludeTakingSchedule((value) => !value);
              }}
              className="flex-row items-center justify-between"
              style={toggleRowStyle}
            >
              <View style={{ flex: 1, paddingRight: 12 }}>
                <CardTitleText>
                  {t("add_medication.inventory.also_create_a_dose_schedule")}
                </CardTitleText>
                <CardSubtitleText>
                  {t("add_medication.inventory.adds_doses_to_your_calendar")}
                </CardSubtitleText>
              </View>
              <MoonToggle
                value={includeTakingSchedule}
                onChange={setIncludeTakingSchedule}
                accessibilityLabel={t(
                  "add_medication.inventory.also_create_a_dose_schedule",
                )}
                action="toggle_taking_schedule"
              />
            </Pressable>
          </>
        ) : null}

        {showScheduleFields ? (
          <>
            {/* Start date */}
            <View
              style={{ marginBottom: 16 }}
              onLayout={(event) => {
                if (confirmingStartDate) {
                  configureScrollRef.current?.scrollTo({
                    y: Math.max(0, event.nativeEvent.layout.y - 16),
                    animated: false,
                  });
                }
              }}
            >
              <DateField
                key={startDateInputSession}
                label={t("add_medication.start_date")}
                value={startDate}
                onChange={(date) => {
                  setStartDate(date);
                  // Empty is a reviewable draft. Partial typing remains invalid
                  // because DateField does not emit it through onChange.
                  setIsStartDateInputValid(true);
                }}
                onValidityChange={setIsStartDateInputValid}
                hint={
                  !startDate
                    ? t("add_medication.confirm_start_date_to_schedule")
                    : undefined
                }
              />
              <View
                style={{
                  flexDirection: "row",
                  flexWrap: "wrap",
                  gap: 8,
                  marginTop: 8,
                }}
              >
                {[
                  {
                    date: initialStartDate,
                    label: t("add_medication.selected_calendar_day"),
                  },
                  {
                    date: menstruationStartDate,
                    label: t("add_medication.period_day_one"),
                  },
                  {
                    // The timing-plan API uses the protocol phase as the
                    // stimulation-day anchor; do not substitute cycle.startDate.
                    date: cyclePhases.find((phase) => phase.type === "protocol")
                      ?.startDate,
                    label: t("add_medication.stimulation_day_one"),
                  },
                  {
                    date: getToday(timezone),
                    label: t("add_medication.start_today"),
                  },
                ]
                  .filter(
                    (option, index, options) =>
                      option.date &&
                      options.findIndex(
                        (other) => other.date === option.date,
                      ) === index &&
                      (!cycleDateBounds ||
                        (option.date >= cycleDateBounds.startDate &&
                          (!cycleDateBounds.endDate ||
                            option.date <= cycleDateBounds.endDate))),
                  )
                  .map((option) => (
                    <MoonFilterChip
                      key={option.date}
                      label={option.label}
                      selected={startDate === option.date}
                      action="medication_start_date_shortcut"
                      onPress={() => {
                        if (option.date) {
                          setStartDate(option.date);
                          setIsStartDateInputValid(true);
                          // A shortcut also replaces an incomplete typed edit,
                          // even when its ISO date equals the last valid value.
                          setStartDateInputSession((session) => session + 1);
                        }
                      }}
                    />
                  ))}
                {!isEditMode ? (
                  <MoonFilterChip
                    label={t("add_medication.start_date_not_known_yet")}
                    selected={!startDate && isStartDateInputValid}
                    action="medication_start_date_unknown"
                    onPress={() => {
                      setStartDate("");
                      setIsStartDateInputValid(true);
                      setStartDateInputSession((session) => session + 1);
                    }}
                  />
                ) : null}
              </View>
            </View>

            {/* Duration chips */}
            <MoonFormField label={t("add_medication.duration")}>
              <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 8 }}>
                {[
                  ...DURATION_PRESETS.map((preset) => ({
                    id: preset.labelKey,
                    label: t(preset.labelKey),
                    action: "duration_preset",
                  })),
                  {
                    id: "Custom",
                    label: t("add_medication.custom"),
                    action: "add_medication.custom",
                  },
                ].map((option) => (
                  <MoonFilterChip
                    key={option.id}
                    label={option.label}
                    selected={durationPreset === option.id}
                    // Tapping the selected duration again clears it.
                    onPress={() =>
                      setDurationPreset(
                        durationPreset === option.id ? null : option.id,
                      )
                    }
                    action={option.action}
                  />
                ))}
              </View>
            </MoonFormField>
            {durationPreset === "Custom" && (
              // The unit sits inside the field, as weight and height do.
              <TextField
                value={customDurationValue}
                onChangeText={setCustomDurationValue}
                placeholder={t("add_medication.custom_duration_placeholder")}
                accessibilityLabel={t(
                  "add_medication.custom_duration_placeholder",
                )}
                keyboardType="number-pad"
                style={{ marginTop: 10 }}
                trailing={
                  <MoonSegmentedControl
                    tabs={(["days", "weeks", "months"] as const).map(
                      (unit) => ({
                        value: unit,
                        label: t(`add_medication.duration_unit_${unit}`),
                      }),
                    )}
                    active={customDurationUnit}
                    onChange={setCustomDurationUnit}
                    shape="rounded"
                    style={{ marginTop: 0, marginRight: 4 }}
                  />
                }
              />
            )}
            {/* In edit mode a finite course arrives as a bare endDate with no
                duration preset — the date must stay visible or the preserved
                course reads as "Ongoing" (#2218). */}
            {computedEndDate &&
            (editMedication != null ||
              (durationPreset &&
                durationPreset !==
                  "add_medication.duration_preset.ongoing")) ? (
              <Text
                className="font-dm-sans"
                style={{
                  fontSize: 12,
                  color: EMBER.colors.textLight,
                  marginTop: 6,
                }}
              >
                Ends {formatDate(computedEndDate)}
              </Text>
            ) : null}
            <View style={{ marginBottom: 16 }} />

            {/* Time of day + custom time entry: the same option pills as the
                duration, holding several times at once. Two to a row, so
                "Afternoon (12:00)" fits on one line. */}
            <MoonFormField label={t("add_medication.time_of_day")}>
              <InlineOptionPicker
                options={[
                  ...TIME_PRESETS.map((preset) => ({
                    id: preset.value,
                    label: `${t(preset.labelKey)} (${preset.value})`,
                    icon: preset.icon,
                    action: "time_of_day_preset",
                  })),
                  {
                    id: CUSTOM_TIME_OPTION,
                    label: t("add_medication.custom"),
                    icon: Clock,
                    action: "custom_time_of_day",
                  },
                ]}
                value={
                  showCustomTimeInput
                    ? [...selectedTimes, CUSTOM_TIME_OPTION]
                    : selectedTimes
                }
                onChange={(option) =>
                  option === CUSTOM_TIME_OPTION
                    ? setShowCustomTimeInput(!showCustomTimeInput)
                    : handleTimeToggle(option)
                }
                wrap
                columns={2}
              />
            </MoonFormField>

            {/* Custom time input */}
            {showCustomTimeInput && (
              <View
                className="flex-row items-center"
                style={{
                  gap: 8,
                  marginTop: 10,
                }}
              >
                <View style={{ flex: 1 }}>
                  <MoonValueInput
                    value={customTimeValue}
                    onChangeText={setCustomTimeValue}
                    placeholder={t("add_medication.custom_time_placeholder")}
                    accessibilityLabel={t(
                      "add_medication.custom_time_placeholder",
                    )}
                  />
                </View>
                <CompactActionButton
                  label={t("add_medication.add")}
                  action="add_medication.add"
                  variant="primary"
                  disabled={!isValidTime(customTimeValue.trim())}
                  onPress={() => {
                    const trimmed = customTimeValue.trim();
                    if (
                      isValidTime(trimmed) &&
                      !selectedTimes.includes(trimmed)
                    ) {
                      setSelectedTimes((prev) => [...prev, trimmed]);
                      setCustomTimeValue("");
                    }
                  }}
                />
              </View>
            )}

            {/* Custom time tags (removable) */}
            {selectedTimes.filter(
              (t) => !TIME_PRESETS.some((p) => p.value === t),
            ).length > 0 && (
              <View
                style={{
                  flexDirection: "row",
                  flexWrap: "wrap",
                  gap: 8,
                  marginTop: 8,
                }}
              >
                {selectedTimes
                  .filter((time) => !TIME_PRESETS.some((p) => p.value === time))
                  .map((time) => {
                    const removeTime = () =>
                      setSelectedTimes((prev) =>
                        prev.filter((x) => x !== time),
                      );
                    return (
                      <MoonFilterChip
                        key={time}
                        label={time}
                        selected
                        onPress={removeTime}
                        onRemove={removeTime}
                        removeAccessibilityLabel={t(
                          "add_medication.remove_dose_time",
                          { params: { time } },
                        )}
                        action="remove_dose_time"
                        radius={10}
                        tone="glass"
                        showCheck={false}
                      />
                    );
                  })}
              </View>
            )}

            {/* Multi-dose summary badge */}
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 12,
                color: EMBER.colors.textLight,
                marginTop: 6,
                marginBottom: 16,
              }}
            >
              {formatTimeSummary(selectedTimes, t)}
            </Text>

            {/* Notes */}
            <TextField
              label={t("add_medication.notes_optional")}
              style={{ marginBottom: 16 }}
              value={notes}
              onChangeText={setNotes}
              placeholder={t("add_medication.notes_placeholder")}
              numberOfLines={3}
              notes
            />

            {/* Link to cycle toggle */}
            {cycleId ? (
              <View
                className="flex-row items-center justify-between"
                style={{
                  paddingVertical: 12,
                  marginBottom: 16,
                }}
              >
                <Text
                  className="font-dm-sans"
                  style={{ fontSize: 14, color: EMBER.colors.text }}
                >
                  {t("add_medication.link_to_cycle")}
                </Text>
                <MoonToggle value={linkToCycle} onChange={setLinkToCycle} />
              </View>
            ) : null}
          </>
        ) : null}

        <PrimaryButton
          label={t(
            ocrMedQueue.length > 0
              ? "add_medication.prescription_done_editing"
              : "add_medication.review",
          )}
          onPress={() => {
            if (!name.trim()) return;
            clearScanError();
            if (ocrMedQueue.length > 0) {
              const dose = parseDoseInput(dosageAmount);
              if (!dose.ok) {
                setDoseError(dose.reason);
                return;
              }
              rememberPrescriptionDraft();
              setPrescriptionError("");
              setPrescriptionAddFailed(false);
              setStep(1);
            } else setStep(3);
          }}
          disabled={
            !name.trim() || (showScheduleFields && !isStartDateInputValid)
          }
        />
      </ScrollView>
    );
  }

  // ============================================================================
  // Step 3 — Review & Confirm
  // ============================================================================

  function renderStep3() {
    const isSubmitting =
      createMedication.isPending || updateMedication.isPending;
    const showScheduleFields = !inventoryMode || includeTakingSchedule;
    const inventoryEntry = inventoryMode ? buildInventoryEntry() : null;
    const submitLabel = isEditMode
      ? t("add_medication.submit_edit")
      : inventoryMode && includeTakingSchedule
        ? t("add_medication.inventory.submit_add_with_schedule")
        : inventoryMode
          ? t("add_medication.inventory.submit_add_only")
          : effectiveIsTriggerShot
            ? t("add_medication.submit_add_trigger")
            : t("add_medication.submit_add");

    const summaryRows: { icon: LucideIcon; label: string; value: string }[] = [
      { icon: Pill, label: t("add_medication.medication_name"), value: name },
    ];
    if (effectiveIsTriggerShot) {
      summaryRows.push({
        icon: Syringe,
        label: t("add_medication.summary_role"),
        value: t("add_medication.summary_role_trigger_shot"),
      });
    }
    if (dosageAmount) {
      const unitDisplay = dosageUnit
        ? ` ${DOSE_UNIT_KEYS[dosageUnit] ? t(DOSE_UNIT_KEYS[dosageUnit]) : dosageUnit}`
        : "";
      summaryRows.push({
        icon: Pill,
        label: t("add_medication.summary_dosage"),
        value: `${dosageAmount}${unitDisplay}`,
      });
    }
    if (route) {
      const routeOpt = ROUTE_OPTIONS.find((o) => o.value === route);
      summaryRows.push({
        icon: Route,
        label: t("add_medication.summary_route"),
        value: routeOpt ? t(routeOpt.labelKey) : route,
      });
    }
    if (deliveryMethod) {
      const dmOpt = INJECTION_DELIVERY_METHODS.find(
        (m) => m.value === deliveryMethod,
      );
      if (dmOpt) {
        summaryRows.push({
          icon: Syringe,
          label: t("add_medication.summary_delivery_method"),
          value: t(dmOpt.labelKey),
        });
      }
    }
    if (inventoryEntry) {
      summaryRows.push({
        icon: Package,
        label: t("add_medication.inventory.stock_on_hand_label"),
        value: `${inventoryEntry.quantity} ${inventoryEntry.unitLabel}`,
      });
    }
    if (showScheduleFields) {
      summaryRows.push({
        icon: CalendarDays,
        label: t("add_medication.summary_start_date"),
        value: startDate
          ? formatDate(startDate)
          : t("add_medication.start_date_to_confirm"),
      });
    }
    if (showScheduleFields && durationPreset) {
      const durPreset = DURATION_PRESETS.find(
        (p) => p.labelKey === durationPreset,
      );
      summaryRows.push({
        icon: CalendarDays,
        label: t("add_medication.summary_duration"),
        value:
          durationPreset === "Custom"
            ? `${customDurationValue} ${customDurationUnit}`
            : durPreset
              ? t(durPreset.labelKey)
              : durationPreset,
      });
    }
    if (showScheduleFields && computedEndDate) {
      summaryRows.push({
        icon: CalendarDays,
        label: t("add_medication.summary_end_date"),
        value: formatDate(computedEndDate),
      });
    }
    if (showScheduleFields) {
      summaryRows.push({
        icon: Clock,
        label: t("add_medication.prescription_reminders"),
        value: formatTimeSummary(selectedTimes, t),
      });
    }
    if (showScheduleFields && notes) {
      summaryRows.push({
        icon: NotebookText,
        label: t("add_medication.summary_notes"),
        value: notes,
      });
    }
    if (showScheduleFields && cycleId && linkToCycle) {
      summaryRows.push({
        icon: Link2,
        label: t("add_medication.summary_linked_to_cycle"),
        value: t("add_medication.summary_yes"),
      });
    }

    return (
      <ScrollView
        contentContainerStyle={{ padding: 24 }}
        showsVerticalScrollIndicator={false}
      >
        {effectiveIsTriggerShot ? (
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 15,
              color: EMBER.colors.textMid,
              textAlign: "center",
              marginBottom: 20,
            }}
          >
            {t("add_medication.trigger_review_intro")}
          </Text>
        ) : null}

        <CardSurface
          testID="medication-review-course"
          style={{ padding: 16, marginBottom: 24 }}
        >
          <Row
            surface="plain"
            title={name}
            leading={medicationIllustration(name, 40)}
            leadingSize={40}
            subtitle={getMedicationByBrandName(name)?.body.genericName}
            trailing={
              <TextActionButton
                label={t("add_medication.edit_details")}
                action="edit_medication_details"
                size="sm"
                onPress={() => setStep(2)}
              />
            }
            chevron={false}
            paddingHorizontal={0}
            paddingVertical={0}
            style={{ marginBottom: 20 }}
          />
          <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 16 }}>
            {[
              ...summaryRows.filter(
                (row) => row.label !== t("add_medication.medication_name"),
              ),
            ].map((row) => (
              <MoonDetailRow
                key={row.label}
                icon={row.icon}
                label={row.label}
                value={
                  row.label === t("add_medication.summary_notes") ? (
                    <CardSubtitleText>{row.value}</CardSubtitleText>
                  ) : (
                    row.value
                  )
                }
                style={{
                  flexGrow: 1,
                  flexBasis:
                    row.label === t("add_medication.summary_notes")
                      ? "100%"
                      : "44%",
                }}
              />
            ))}
          </View>
        </CardSurface>

        <View style={{ gap: 10 }}>
          {saveError?.kind === "date_outside_cycle" ? (
            <ActionBanner
              variant="important"
              title={t("add_medication.historical_date_error_title")}
              message={t("add_medication.historical_date_error_message")}
            />
          ) : saveError?.kind === "save_failed" ? (
            <ActionBanner
              variant="important"
              title={t("add_medication.alert_update_failed_title")}
              message={t("add_medication.alert_update_failed_message")}
              actionLabel={t("clinical_data.retry")}
              action="retry_save_medication"
              actionIcon={RotateCcw}
              actionLoading={isSubmitting}
              onAction={() =>
                void (saveError.retry === "submit_and_add_another"
                  ? handleSubmitAndAddAnother()
                  : handleSubmit())
              }
            />
          ) : saveError?.kind === "stock_not_added" ? (
            <ActionBanner
              variant="important"
              title={t("add_medication.inventory.stock_not_added")}
              message={t(
                "add_medication.inventory.medication_is_saved_only_stock_failed",
              )}
              actionLabel={t("clinical_data.retry")}
              action="retry_add_medication_stock"
              actionIcon={RotateCcw}
              actionLoading={isSubmitting}
              onAction={() => void handleSubmit()}
            />
          ) : null}
          <PrimaryButton
            label={
              showScheduleFields && !startDate
                ? t("add_medication.confirm_start_date")
                : submitLabel
            }
            onPress={() => void handleSubmit()}
            loading={isSubmitting}
          />

          {!isEditMode &&
          !inventoryMode &&
          !effectiveIsTriggerShot &&
          ocrMedQueue.length === 0 &&
          startDate ? (
            <View>
              <SecondaryButton
                label={t("add_medication.save_add_different_dose")}
                onPress={handleSubmitAndAddAnother}
                disabled={isSubmitting}
              />
            </View>
          ) : null}
        </View>
      </ScrollView>
    );
  }

  // ============================================================================
  // Duplicate-detection modal handlers
  // ============================================================================

  async function handleDuplicateAddAnyway() {
    setDuplicateMatch(null);
    await handleSubmit({ bypassDuplicateCheck: true });
  }

  async function handleDuplicateReplace() {
    const match = duplicateMatch;
    if (!match) return;
    // Save the new course first; only stop the old one once we know the
    // replacement actually took. Reversing this order could leave the patient
    // with the old course stopped and no new one if validation or the server
    // rejects the new entry.
    setDuplicateMatch(null);
    await handleSubmit({ bypassDuplicateCheck: true, replacing: match });
  }

  function handleDuplicateGoBack() {
    setDuplicateMatch(null);
  }

  // ============================================================================
  // Header
  // ============================================================================

  const entryTitle = inventoryMode
    ? t("add_medication.inventory.step_title_add")
    : isTriggerShotContext
      ? t("add_medication.trigger_step_title_add")
      : t("add_medication.step_title_add");
  const stepTitles: Record<Step, string> = {
    0: entryTitle,
    1: t("add_medication.review_prescription"),
    2:
      confirmingStartDate && name.trim()
        ? name
        : isEditMode
          ? t("add_medication.step_title_edit")
          : entryTitle,
    3: inventoryMode
      ? t("add_medication.inventory.step_title_review")
      : isTriggerShotContext
        ? t("add_medication.trigger_step_title_review")
        : t("add_medication.step_title_review"),
  };

  const headerArtwork =
    ocrMedQueue.length > 1
      ? ocrMedQueue.map((med, index) =>
          index === ocrMedIndex && step !== 1
            ? medicationHeaderArtwork(name, deliveryMethod)
            : medicationHeaderArtwork(
                prescriptionDrafts.current.get(index)?.name ??
                  med.medicationName,
                prescriptionDrafts.current.get(index)?.deliveryMethod ??
                  med.deliveryMethod,
              ),
        )
      : [
          medicationHeaderArtwork(
            medicationSelected || isEditMode ? name : "",
            medicationSelected || isEditMode ? deliveryMethod : "",
          ),
        ];
  const hasMultipleHeaderMedications = headerArtwork.length > 1;

  const canGoBack = !isSavingPrescription && !isEditMode && step > 0;
  const dialogMaxHeight = windowHeight * 0.85;
  // Keep the shared Dialog surface unchanged while giving this long,
  // form-heavy dialog a light content wash like the layered Care detail
  // dialogs. The wash is intentionally not another backdrop-filter layer:
  // that would create a stacking context and interfere with anchored menus
  // that must render above the dialog.
  const medicationContentSurfaceStyle = {
    backgroundColor: "rgba(255,255,255,0.18)",
  };

  return (
    <React.Fragment>
      <Dialog
        containerStyle={{ maxHeight: dialogMaxHeight }}
        visible={visible}
        onClose={handleClose}
        layout="centered"
        closeButton="minimal"
        surfaceStyle={{ maxHeight: dialogMaxHeight }}
        contentStyle={{
          maxHeight: dialogMaxHeight,
          ...medicationContentSurfaceStyle,
          paddingTop: 0,
          paddingHorizontal: 0,
          paddingBottom: 0,
        }}
        testID="medication-dialog"
      >
        <View
          testID="medication-dialog-header"
          style={{
            alignItems: "center",
            justifyContent: "center",
            minHeight: 52,
            paddingHorizontal: 52,
            paddingTop: 16,
            gap: 8,
          }}
        >
          {canGoBack ? (
            <TextActionButton
              label={t("add_medication.back")}
              action="add_medication.back"
              tone="tertiary"
              onPress={() => {
                clearScanError();
                setSaveError(null);
                if (step === 3) setStep(2);
                else if (step === 2 && ocrMedQueue.length > 0) {
                  rememberPrescriptionDraft();
                  setStep(1);
                } else resetState();
              }}
              // Less the button's own 16 x 12 padding, so the label sits
              // 20 x 19 from the corner.
              style={{
                position: "absolute",
                left: 4,
                top: 7,
              }}
            />
          ) : null}
          <ScrollView
            horizontal
            testID="medication-dialog-artwork"
            showsHorizontalScrollIndicator={false}
            style={{ width: "100%", height: 64, flexGrow: 0 }}
            contentContainerStyle={{
              flexGrow: 1,
              alignItems: "center",
              justifyContent: "center",
              paddingHorizontal: 8,
            }}
          >
            {headerArtwork.map((artwork, index) =>
              hasMultipleHeaderMedications ? (
                <View
                  key={`${index}-${artwork.kind}`}
                  testID={`medication-header-circle-${index}`}
                  style={[
                    medicationHeaderCircleSurfaceStyle,
                    {
                      width: 56,
                      height: 56,
                      borderRadius: 28,
                      alignItems: "center",
                      justifyContent: "center",
                      marginLeft: index === 0 ? 0 : -12,
                    },
                  ]}
                >
                  <DialogIcon
                    source={artwork.source}
                    width={44}
                    height={44}
                    testID={`medication-dialog-artwork-${index}`}
                  />
                </View>
              ) : (
                <DialogIcon
                  key={`${index}-${artwork.kind}`}
                  source={artwork.source}
                  width={64}
                  height={64}
                  testID={`medication-dialog-artwork-${index}`}
                />
              ),
            )}
          </ScrollView>
          <DialogTitleText style={{ textAlign: "center" }}>
            {stepTitles[step]}
          </DialogTitleText>
          {step === 1 ? (
            <DialogBodyText style={{ textAlign: "center", marginTop: 0 }}>
              {t(
                `add_medication.medications_found_in_prescription_${ocrMedQueue.length === 1 ? "one" : "other"}`,
                { params: { count: String(ocrMedQueue.length) } },
              )}
            </DialogBodyText>
          ) : null}
        </View>

        {step === 0 && renderStep0()}
        {step === 1 && renderPrescriptionRecap()}
        {step === 2 && renderStep2()}
        {step === 3 && renderStep3()}
      </Dialog>
      <Dialog
        visible={!!duplicateMatch}
        onClose={handleDuplicateGoBack}
        layout="centered"
        closeButton={false}
        testID="medication-duplicate-dialog"
      >
        <View style={{ paddingHorizontal: 16, paddingVertical: 20, gap: 12 }}>
          <DialogTitleText style={{ textAlign: "center" }}>
            {t("add_medication.duplicate_title")}
          </DialogTitleText>
          <DialogBodyText style={{ textAlign: "center" }}>
            {t("add_medication.duplicate_body", {
              params: {
                name: duplicateMatch?.name ?? "",
                time: duplicateMatch?.time ?? "",
              },
            })}
          </DialogBodyText>
          <View style={{ gap: 8, marginTop: 12 }}>
            <PrimaryButton
              label={t("add_medication.duplicate_add_anyway")}
              onPress={handleDuplicateAddAnyway}
              loading={createMedication.isPending}
              testID="medication-duplicate-add-anyway"
            />
            <SecondaryButton
              label={t("add_medication.duplicate_replace")}
              onPress={handleDuplicateReplace}
              loading={updateMedication.isPending}
              testID="medication-duplicate-replace"
            />
            <TextActionButton
              label={t("add_medication.duplicate_go_back")}
              action="add_medication.duplicate_go_back"
              tone="tertiary"
              onPress={handleDuplicateGoBack}
              testID="medication-duplicate-go-back"
            />
          </View>
        </View>
      </Dialog>
    </React.Fragment>
  );
}
